import { DatePipe, NgClass, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalendarMonthViewDay } from 'angular-calendar';
import moment from 'moment';
import { firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, switchMap, takeWhile } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { APPOINTMENT_CONSTANT } from '../../../constants/appointment-constants';
import { DATE_ISO_FORMAT, DATE_TIME_ISO_T_FORMAT } from '../../../constants/date.constants';
import { TEMPLATE_CONSTANT } from '../../../constants/template.constant';
import { TRANSLATION_TEMPLATES } from '../../../constants/translation-templates-constants';
import { APPOINTMENT_SOURCE_ID, BOOKING_CUSTOM_FIELD_TYPES, BOOKING_REDIRECTION_VIEWS, CAPTCHA_TYPE, GTM_EVENTS, LOCAL_STORAGE_CONSTANTS, PAYMENT_TYPES, WIDGET_CONSTANTS, WIDGET_DISPLAY_TIMEZONE_SOURCE, WINDOW_LISTENER_CONSTANTS } from '../../../constants/widget-constants';
import { CustomFieldOptions, CustomFieldsDao } from '../../../db-models/appointment-custom-fields-dao';
import { AppointmentReservationBody } from '../../../db-models/appointment-reservation.model';
import { AppointmentServiceDao } from '../../../db-models/appointment-service-dao';
import { CouponDbModel } from '../../../db-models/coupon-db.model';
import { AppointmentsSlotsDao } from '../../../db-models/free-appointment-dao';
import { PayPalOrderDetailModel, PayPalSettingsDbModel } from '../../../db-models/partner-setting.model';
import { StoresDao } from '../../../db-models/stores-dao';
import { TimezoneDbModel } from '../../../db-models/timezone.model';
import { WidgetAppointmentGroupModel } from '../../../db-models/widget-appointment-group.dao';
import { AppointmentAnalyticsDao, CustomBookingMessageTemplate, PartnerDao, WidgetBookingInfoDao, WorkerDao } from '../../../db-models/widget-conf-dao';
import { AppointmentBookItem, CartItem } from '../../../models/cart.model';
import { GlobalObjects } from '../../../models/global';
import { AdyenPaymentDetails } from '../../../models/adyen.model';
import { AdyenResumeFailure, AdyenResumeReturn, AdyenResumeSnapshot } from '../../../models/adyen-resume.model';
import { AdyenResumeService } from '../../../services/adyen-resume.service';
import { buildAdyenHoldScope } from '../../../services/adyen-booking-context.util';
import { canBookAfterAdyenPayment, isCaptchaChallengePending, reservationExpiredMessageKey } from '../../../services/adyen-payment-booking.util';
import { SaferPayPaymentDetailsDbModel } from '../../../models/saferpay.model';
import { AppointmentState, UserState } from '../../../models/state.model';
import { WidgetColorConf } from '../../../models/widget-color.model';
import { CustomerPrefillDataModel } from '../../../models/widget-conf';
import { AppointmentCartService } from '../../../services/appointment-cart.service';
import { AppointmentService } from '../../../services/appointment.service';
import { CaptchaService } from '../../../services/captcha.service';
import { CustomEventService } from '../../../services/custom-event.service';
import { CustomFieldService } from '../../../services/custom-field.service';
import { DateUtilsService } from '../../../services/date-utils.service';
import { FormsService } from '../../../services/forms.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { HelperService } from '../../../services/helper.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { LoggerService } from '../../../services/logger.service';
import { PartnerService } from '../../../services/partner.service';
import { PaymentService } from '../../../services/payment.service';
import { workerServesStore } from '../../../services/store-scope.util';
import { TimezoneService } from '../../../services/timezone.service';
import { UtilService } from '../../../services/util.service';
import { VatResult, calculateIncludedVat, calculateVat, isVatAddedOnTop, vatTypeLabelKey } from '../../../services/vat-calculation.util';
import { WidgetUtilService } from '../../../services/widget-util.service';
import { AlertComponent } from '../../../shared/components/alert/alert.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { FinalpageComponent } from '../../../shared/components/finalpage/finalpage.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { NewAlertComponent } from '../../../shared/components/new-alert/new-alert.component';
import { AddressFormatPipe } from '../../../shared/pipes/address-format.pipe';
import { CalioCurrencyPipe } from '../../../shared/pipes/calio-currency.pipe';
import { CalioMeetingTemplatePipe } from '../../../shared/pipes/calio-meeting-template.pipe';
import { DateUtcPipe } from '../../../shared/pipes/date_utc.pipe';
import { HourReplacePipe } from '../../../shared/pipes/hour-replace.pipe';
import { MinuteReplacePipe } from '../../../shared/pipes/minute-replace.pipe';
import { ReplaceCommaPipe } from '../../../shared/pipes/replace-comma.pipe';
import { TranslationPipe } from '../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../shared/pipes/trust-html.pipe';
import { CwPaymentMethodsComponent } from '../../common/cw-payment-methods/cw-payment-methods.component';
import { NewCalendarPickerComponent } from '../../common/new-calendar-picker/new-calendar-picker.component';
import { ReservationCountdownComponent } from '../../common/reservation-countdown/reservation-countdown.component';
import { CompactPersonalFormComponent } from './compact-personal-form/compact-personal-form.component';
import { CompactSlotsComponent } from './compact-slots/compact-slots.component';
import { getDomRoot } from '../../../services/dom-root.util';
import {
  adyenErrorMessageKey,
  isAlreadyBookedWithPayment,
  isBookingOutcomeUnknown,
  isPaidBookingStillCompleting,
  isPaymentSessionSpent,
  REFUND_NOTICE_KEY,
  refundNoticeKeyFor,
} from '../../../services/booking-outcome.util';
import { isInvoiceAddressMandatoryFor } from '../../../services/invoice-address.util';

const component = [NewCalendarPickerComponent, CompactSlotsComponent, CompactPersonalFormComponent, ButtonComponent, AlertComponent, CwPaymentMethodsComponent, FinalpageComponent, LoaderComponent, NewAlertComponent, ReservationCountdownComponent];
const module = [FormsModule, FontAwesomeModule, TranslateModule,];
const pipe = [TranslationPipe, TrustHtmlPipe, CalioCurrencyPipe, CalioMeetingTemplatePipe, DateUtcPipe, ReplaceCommaPipe, MinuteReplacePipe, DatePipe, HourReplacePipe, TitleCasePipe, AddressFormatPipe];
const ngModule = [NgTemplateOutlet, NgClass, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownItem]

@Component({
  selector: 'app-compact-widget',
  templateUrl: './compact-widget.component.html',
  styleUrls: ['./compact-widget.component.scss'],
  imports: [...component, ...module, ...pipe, ...ngModule]
})
export class CompactWidgetComponent implements OnInit, OnDestroy {
  protected readonly vatTypeLabelKey = vatTypeLabelKey;

  @Input() partner: PartnerDao;
  @Input() worker: WorkerDao;
  @Input() selectedAppointmentServiceIds: string[] = [];
  @Input() widgetColorConf: WidgetColorConf;
  @Input() calendarPreselectedDate: string;
  @Input() dateContext: string;
  @Input() dateContextStart: string;
  @Input() dateContextEnd: string;
  @Input() selectedJsonCustomFields: {
    [key: number]: { type: string; value: any } | any;
  };
  @Input() selectedJsonCustomerDetail: CustomerPrefillDataModel;
  @Input() selectedStoreId: number;
  @Input() langSwitcher = true;
  @Input() globals: GlobalObjects;
  @Input() showSubscriptionError = false;
  @Input() customer_notification_preference: string;
  @Input() bookerWorkerId: number;
  @Input() utmSource: string;
  @Input() utmMedium: string;
  @Input() utmCampaign: string;
  @Input() utmContent: string;
  @Input() utmTerm: string;
  @Input() utmId: string;
  @Input() isBookingDisabled = false;
  @Input() isFrame = false;
  @Input() widgetConf: any;
  @Input() lang: string;
  @Input() widgetBookingInfo: WidgetBookingInfoDao;
  @Input() meeting_type_id: string;
  @Input() bookingType: string;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() preApplyCoupon: string;
  @Input() timezones = new Array<TimezoneDbModel>();
  @Input() redirectedPaymentIntent: string;
  @Input() redirectedPaymentIntentClientSecret: string;
  @Input() appointmentStripeRedirectionData: Record<string, any>;
  /** An Adyen payment this tab parked before leaving the page, now back (see AdyenResumeService). */
  @Input() adyenResume?: AdyenResumeReturn;

  @Output() bookingSuccessEvent = new EventEmitter<any>();

  @ViewChild('cbcwWidgetWrapper') cbcwWidgetWrapperRef: ElementRef;
  @ViewChild('appointmentForm') public appointmentForm: NgForm;

  private appointmentService = inject(AppointmentService);
  private cartService = inject(AppointmentCartService);
  private customEventService = inject(CustomEventService);
  private customFieldService = inject(CustomFieldService);
  private dateUtilsService = inject(DateUtilsService);
  private destroyRef = inject(DestroyRef);
  private elementRef = inject(ElementRef);
  private formService = inject(FormsService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private helperService = inject(HelperService);
  private localStorageService = inject(LocalStorageService);
  private partnerService = inject(PartnerService);
  private paymentService = inject(PaymentService);
  /** Book waits while the payment form is down behind a "check your email first" notice. */
  protected readonly payAgainRequired = this.paymentService.payAgainRequired;
  private translateService = inject(TranslateService);
  private adyenResumeService = inject(AdyenResumeService);
  /** What cw-adyen parks before an Adyen payment leaves the page: the Stripe redirect snapshot. */
  protected readonly adyenResumeSnapshot = (): AdyenResumeSnapshot => ({
    host: 'compact',
    data: this.buildRedirectionSnapshot(),
  });
  private utilService = inject(UtilService);
  private widgetUtilService = inject(WidgetUtilService);
  private captchaService = inject(CaptchaService);
  private timezoneService = inject(TimezoneService);

  protected isWhitelabel(): boolean {
    return this.partnerService.hasFeature('is_whitelabel_enabled');
  }

  // When partner has opted into 'partner' mode, slot-fetch & booking payloads carry the partner's
  // configured timezone regardless of visitor's browser TZ. Falls back to visitor TZ otherwise.
  private get effectiveDisplayTimezone(): string {
    if (this.partner?.widget_display_timezone_source === WIDGET_DISPLAY_TIMEZONE_SOURCE.PARTNER && this.partner?.timezone) {
      return this.partner.timezone;
    }
    return this.userState?.timezone ?? this.timezoneService.localTimezone;
  }

  readonly logoBaseUrl = environment.logoBaseUrl;
  readonly workerAvatarUrl = environment.workerAvatarUrl;
  readonly baseUrl = environment.baseUrl;
  readonly footerLogoUrl = environment.footerLogoUrl;
  readonly widgetConstants = WIDGET_CONSTANTS;
  readonly gtmEvents = GTM_EVENTS;
  readonly deployUrl = environment.deployUrl;
  readonly appointmentConstant = APPOINTMENT_CONSTANT;
  readonly dateIsoFormat = DATE_ISO_FORMAT;
  readonly templateContent = TRANSLATION_TEMPLATES;
  readonly bookingCustomFieldTypes = BOOKING_CUSTOM_FIELD_TYPES;
  readonly paymentTypes = PAYMENT_TYPES;
  protected readonly bookingRedirectionViews = BOOKING_REDIRECTION_VIEWS;
  protected readonly templateConstant = TEMPLATE_CONSTANT;
  protected readonly isVatAddedOnTop = isVatAddedOnTop;

  get timeFormat(): string {
    return this.dateUtilsService.userTimeFormat.moment;
  }

  private CART_LS_NAME: string;
  private STATE_LS_NAME: string;

  USER_LS_NAME: string;
  CUSTOMFIELDS_LS_NAME: string;
  alive: boolean;
  booked = false;
  service: AppointmentServiceDao;
  store: StoresDao
  noWorkerFound = false;
  noServiceFound = false;
  loadingService = true;
  cartSupported = false;
  viewDate: Date;
  now = new Date();
  disablePrev = true;
  monthChanged = false;
  lastCalendarMonth: number;
  excludeWeekDays: number[] = [];
  exceptionDays: Date[];
  maxCalendarAvailabilityDate: Date;
  calendarLoaded = false;
  calendarDate = false;
  days: CalendarMonthViewDay[] = [];
  showBookableDateNotAvailableIssue = false;
  automaticBookableDateProcessIsEnabled = false;
  automationBookingDatesLimitReached = false;
  oldSelectCalendarDate: CalendarMonthViewDay = null;
  freeAppointments: {
    dayPeriod: string;
    appointments: AppointmentsSlotsDao[];
    showDayPeriodLabel?: boolean;
    widgetGroup: WidgetAppointmentGroupModel;
  };
  freeAppointmentsCount = 0;
  noAppointments = false;
  showAppointmentSlotGhostElement = true;
  appointmentState: AppointmentState = {
    customerProfileId: null,
    store: null,
    categories: [],
    services: [],
    relatedServices: [],
    worker: null,
    date: null,
    termins: null,
    day: null,
    default_worker_id: null,
    leadGeneratorPostcode: null,
    default_worker: null,
  };
  userState: UserState = {
    title: null,
    forename: null,
    lastName: null,
    gender: null,
    eMail: null,
    phone: null,
    comment: null,
    customerId: null,
    customerUuid: null,
    street: null,
    zip: null,
    city: null,
    country: null,
    country_id: null,
    customer_notification_preference: null,
    meeting_type_id: null,
    company: null,
    internal_comment: null,
    captcha_challenge: null,
    timezone: null
  };
  cart: CartItem[] = [];
  calendarSlotsCountList: { date: Date; slotsCount: number }[] = [];
  widgetGroups: WidgetAppointmentGroupModel[] = [];
  showAvailableAppointments = false;
  downloadBookingDetailTemplate: CustomBookingMessageTemplate;
  manageAppointmentNowTemplate: CustomBookingMessageTemplate;
  nothingBookableTemplate: CustomBookingMessageTemplate;
  compactNoServiceTemplate: CustomBookingMessageTemplate;
  bookButtonTemplate: CustomBookingMessageTemplate;
  successTitleTemplate: CustomBookingMessageTemplate;
  successMessage: string;
  courtesyForm: string;
  leadTime: number;
  customFieldValues: {
    [key: number]: | {
      type: string;
      value: any;
      option_values: {
        custom_field_option_id: number;
        custom_field_option_value: string;
      }[];
    } | any;
  } = {};
  tempCustomFields: CustomFieldsDao[] = [];
  appointmentCustomFields: CustomFieldsDao[] = [];
  childCustomFieldsLoadedSubscription: Subscription;
  removeCustomFieldsSubscription: Subscription;
  isCustomFieldsLoaded = false;
  supportedWidgetLanguages: string[];
  currentView = '';
  customerSelected: object = null;
  hideBookingButton = false;
  disableBookingButton = false;
  stripeElementLoading = false;
  isReservationExpired = false;
  /** A /book request has been sent and not answered yet (including its booking-lock resends). */
  private bookingRequestInFlight = false;
  /**
   * An Adyen payment is running: the hosted window is open, or Drop-in is authorising. Back, the
   * cart and Book stay locked until it reports back; the payment's own "Cancel payment" is the way
   * out, and it releases this through onAdyenReset().
   */
  protected readonly adyenPaymentInFlight = signal(false);
  bookingErrorMessage: string;
  stripePaymentMethodId: string;
  showRefundNotice = false;
  refundNoticeKey = REFUND_NOTICE_KEY;
  refundTransactionId: string;
  storageComplete = false;
  coupon: CouponDbModel;
  isInvalidCoupon = false;
  paymentType: string;
  paypalOrderDetail: PayPalOrderDetailModel;
  saferPayPaymentDetails: SaferPayPaymentDetailsDbModel;
  isPaymentInProgress = false;
  successFullBookings: {
    landingPageLink: string;
    bookingUUID: string;
    selectedStore: StoresDao;
  }[] = [];
  finalPage: boolean;
  finalPageError: string;
  priceAfterCouponReduction: number;
  /** VAT on top of the discounted net (`plus`/`exclude` partners only). */
  cartVat = signal<VatResult>({ byRate: [], totalCents: 0 });
  backButtonDisabled = false;
  isServiceOnlinePaymentEnabled = 0;
  isStripeEnabled = false;
  isOnlinePaymentMandatory = false;
  isBexioEnabled = false;
  isExternalInvoiceEnabled = false;
  isStoreEnabled = false;
  isPaypalEnabled = false;
  isSaferpayEnabled = false;
  invoiceAddressChecked = false;
  invoiceAddressValid = true;
  invoiceAddress: AppointmentBookItem['invoice_address'] = undefined;
  isAdyenEnabled = false;
  adyenPaymentDetails: AdyenPaymentDetails;
  paypalSetting: PayPalSettingsDbModel;
  customFieldsAddonsPrice = 0;
  finalCouponDiscount = 0;
  totalCartPrice = 0;
  couponDisableConfirmButton = false;
  showFullscreenOverlay = false;
  availableSlotsArray: AppointmentsSlotsDao[] = [];
  protected bookButtonLoader = false;
  protected appointmentReservationError = false;
  protected stripeConnectedAccountId: string;
  protected defaultPaymentType = '';
  protected showStripeRedirectionLoader = false;

  constructor() {
    this.alive = true;
    this.translateService.onLangChange.subscribe(language => {
      this.lang = language.lang;
      this.createSuccessMessage();
    });
  }

  private setupDates(): void {
    const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();
    this.now = new Date(moment.tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
    const defaultViewDate = new Date(moment.tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
    this.maxCalendarAvailabilityDate = new Date(moment.tz(timezone).add(this.partner.future_booking_threshold_appointments, 'days').format(DATE_TIME_ISO_T_FORMAT));
    this.maxCalendarAvailabilityDate.setHours(23, 59, 59, 999);

    if (this.dateContext && this.dateContextStart && this.dateContextEnd) {
      const startDateContextMoment = moment(this.dateContext, this.dateIsoFormat).tz(timezone).subtract(this.dateContextStart, 'days');
      const endDateContextMoment = moment(this.dateContext, this.dateIsoFormat).tz(timezone).add(this.dateContextEnd, 'days');

      const isInPast = startDateContextMoment.isBefore(moment().tz(timezone));
      const isInFuture = endDateContextMoment.isSameOrAfter(moment().tz(timezone));

      this.viewDate = isInPast && isInFuture ? defaultViewDate : new Date(moment(this.dateContext).startOf('month').format(DATE_TIME_ISO_T_FORMAT));
    } else {
      this.viewDate = defaultViewDate;
    }

    this.lastCalendarMonth = this.viewDate.getMonth();
    this.updateCalendarPrevState(this.viewDate);
  }

  ngOnInit(): void {
    if (this.redirectedPaymentIntent && this.redirectedPaymentIntentClientSecret && this.appointmentStripeRedirectionData) {
      // Handle redirect from Stripe payment verification
      LoggerService.log('[Local Storage] Redirected from Stripe payment verification (Compact booking)');

      // Set payment type to CREDITCARD
      this.paymentType = this.paymentTypes.CREDITCARD;

      // Set stripe payment intent ID from redirected payment intent
      this.stripePaymentMethodId = this.redirectedPaymentIntent;

      // Set loading states
      this.disableBookingButton = true;
      this.bookButtonLoader = true;
      this.bookingErrorMessage = undefined;
      this.showStripeRedirectionLoader = true;

      // Ensure LS keys are set (used on success cleanup)
      this.partner && this.setLocalstorageNames();

      if (this.appointmentStripeRedirectionData?.bookingItems) {
        this.restoreRedirectionSnapshot(this.appointmentStripeRedirectionData);

        // Update stripe_payment_intent_id with the redirected payment intent ID in stored bookingItems
        this.appointmentStripeRedirectionData.bookingItems.forEach((item: AppointmentBookItem) => {
          if (item.payment_type === this.paymentTypes.CREDITCARD) {
            item.stripe_payment_intent_id = this.redirectedPaymentIntent;
          }
        });

        // Proceed with booking directly using STORED booking items (not rebuilding)
        this.processBooking(this.appointmentStripeRedirectionData.bookingItems);
      } else {
        LoggerService.error('[Local Storage] Redirected payment intent exists but stored bookingItems are missing');
        this.bookingErrorMessage = 'Something is wrong with session store data. Please try again.';
        this.disableBookingButton = false;
        this.bookButtonLoader = false;
        this.showStripeRedirectionLoader = false;
      }
    } else if (this.adyenResume) {
      this.resumeAdyenPayment(this.adyenResume);
    }

    this.setupDates();
    if (!this.currentView) {
      this.currentView = this.appointmentConstant.APPOINTMENT;
    }

    if (this.worker === undefined || !this.worker) {
      this.noWorkerFound = true;
    }
    if (this.selectedAppointmentServiceIds === undefined || this.selectedAppointmentServiceIds?.length === 0) {
      this.noServiceFound = true;
    }

    this.setupErrorTemplates();

    if (this.worker) {
      // Skip service loading if returning from Stripe redirect (data already restored)
      if (!this.showStripeRedirectionLoader) {
        this.getService();
      }
      // Setup initial templates
      this.setupInitialTemplates();
      this.setLocalstorageNames();
      this.initFormState();

      this.supportedWidgetLanguages = (this.partner?.supported_widget_languages ? this.partner.supported_widget_languages : []);
      this.cartSupported = this.widgetUtilService.getWidgetConf()?.cartSupported;
      this.courtesyForm = this.widgetUtilService.getWidgetConf()?.courtesyForm;
      this.leadTime = this.widgetUtilService.getWidgetConf()?.appointmentLeadTime;

      // Setup appointmentState object (only if not returning from Stripe redirect)
      if (!this.showStripeRedirectionLoader) {
        // Honour a validly-served selected store (multi-store addon); fall back to the
        // worker's primary store when the worker does not serve the selected store.
        this.appointmentState.store = workerServesStore(this.worker, Number(this.selectedStoreId))
          ? Number(this.selectedStoreId)
          : this.worker.store_id;
        this.appointmentState.worker = this.worker.id;
        this.appointmentState.default_worker_id = this.worker.id;
      }

      this.childCustomFieldsLoadedSubscription = this.customEventService.childCustomFieldsLoadedEvent.subscribe(customFields => {
        this.tempCustomFields = this.tempCustomFields.concat(customFields);
      });

      this.removeCustomFieldsSubscription = this.customEventService.removeChildCustomFieldsEvent.subscribe(ids => {
        this.tempCustomFields = this.tempCustomFields.filter(item => ids.indexOf(item.id) === -1);
      });
    } else {
      this.noWorkerFound = true;
    }
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  private getService(): void {
    // Scope the service list to the SELECTED served store (multi-store addon), falling back to
    // the worker's primary when the worker does not serve it — the same guard appointmentState.store
    // uses. Addon OFF: workerServesStore falls back to [store_id], so this stays the primary store
    // (byte-for-byte the previous behaviour).
    const resolvedStoreId = workerServesStore(this.worker, Number(this.selectedStoreId))
      ? Number(this.selectedStoreId)
      : this.worker.store_id;
    const payload = {
      store_id: resolvedStoreId,
      worker_id: this.worker.id,
    };
    this.formService.getAppointmentServices(payload).pipe(
      takeWhile(() => this.alive)
    ).subscribe({
      next: (services) => {
        this.loadingService = false;
        if (services?.length > 0) {
          const foundService = services.find(
            service => service.is_internal !== 1 && service.id === Number(this.selectedAppointmentServiceIds[0])
          );

          if (foundService) {
            this.service = foundService;

            // Setting store of current worker
            const foundWoker = this.service.services_workers.find(worker => worker.worker_id === this.worker.id);
            if (foundWoker) {
              // Align the store object with the resolved id — otherwise the booking summary and
              // the analytics payload keep reporting the primary store for a secondary-store booking.
              this.store = foundWoker.worker.stores?.find(store => Number(store.id) === Number(this.appointmentState.store))
                ?? foundWoker.worker.store;
              if (Number(this.selectedStoreId) !== this.store.id && !workerServesStore(this.worker, Number(this.selectedStoreId))) {
                LoggerService.warn('Passed store_id', this.selectedStoreId);
                LoggerService.warn('Worker store_id', this.worker.store_id);
                LoggerService.warn('Passed store_id is not matching with worker_id so overwritting it with valid store_id of worker.');
                this.selectedStoreId = this.worker.store_id;
              }
            }

            this.appointmentState.services = [this.service.id];
            this.appointmentState.categories = [this.service.appointment_service_category.id];
          } else {
            LoggerService.warn('Worker do not have associated service.', this.worker.id, this.selectedAppointmentServiceIds);
            this.noServiceFound = true;
          }
        } else {
          this.noServiceFound = true;
        }
      },
      error: (error: HttpErrorResponse) => {
        LoggerService.error(error);
      }
    });
  }

  /** The dead-end messages must resolve even when there is no resource, because that is
   *  exactly when their alerts render. setupInitialTemplates() only runs inside the
   *  `if (this.worker)` branch, so resolving them there left them undefined on the one
   *  path that shows them. */
  private setupErrorTemplates(): void {
    if (!this.widgetTemplates?.length) {
      return;
    }
    this.widgetTemplates.forEach(template => {
      template.is_multi_language = 1;
    });

    this.nothingBookableTemplate = this.widgetTemplates.find(
      template => template.identifier === this.widgetConstants.WIDGET_LABELS.WIDGET_ERROR_NOTHING_BOOKABLE
    );
    this.compactNoServiceTemplate = this.widgetTemplates.find(
      template => template.identifier === this.widgetConstants.WIDGET_LABELS.WIDGET_ERROR_COMPACT_NO_SERVICE
    );
  }

  private setupInitialTemplates(): void {
    // const identifiers = {
    //   WIDGET_NEXT: WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_NEXT,
    //   WIDGET_BOOK_BUTTON: WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_BOOK_BUTTON,
    //   WIDGET_DATE_LABEL: WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_DATE_LABEL,
    //   WIDGET_NO_BOOKABLE_DATES_LABEL: WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_NO_BOOKABLE_DATES_LABEL,
    //   WIDGET_YES_SEARCH_BUTTON_LABEL: WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_YES_SEARCH_BUTTON_LABEL,
    //   WIDGET_BOOKING_INFO_APPOINTMENT: WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_BOOKING_INFO_APPOINTMENT,
    //   WIDGET_MANAGE_BOOKING_LABEL: WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_MANAGE_BOOKING_LABEL,
    //   WIDGET_CHOOSE_DATE_HINT: WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_CHOOSE_DATE_HINT,
    //   WIDGET_NO_POSSIBLE_APPOINTMENTS_HINT: WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_NO_POSSIBLE_APPOINTMENTS_HINT,
    //   PERSONAL_DATA_CARD_TITLE: WIDGET_CONSTANTS.WIDGET_LABELS.PERSONAL_DATA_CARD_TITLE
    // };
    // this.widgetTemplates = this.widgetTemplates.filter(template => identifiers[template.identifier.toUpperCase()]);
    this.widgetTemplates.forEach(template => {
      template.is_multi_language = 1;
    });

    this.bookButtonTemplate = this.widgetTemplates.find(
      template => template.identifier === this.widgetConstants.WIDGET_LABELS.WIDGET_BOOK_BUTTON
    );
    this.successTitleTemplate = this.widgetTemplates.find(
      template => template.identifier === this.widgetConstants.WIDGET_LABELS.WIDGET_SUCCESS_TITLE_LABEL
    );
    this.downloadBookingDetailTemplate = this.widgetTemplates.find(
      template => template.identifier === this.widgetConstants.WIDGET_LABELS.WIDGET_DOWNLOAD_APPOINTMENT_ICS_FILE
    );
    this.manageAppointmentNowTemplate = this.widgetTemplates.find(
      template => template.identifier === this.widgetConstants.WIDGET_LABELS.WIDGET_MANAGE_BOOKING_LABEL
    );
    this.createSuccessMessage();
  }

  private createSuccessMessage(): void {
    const successTemplate = this.widgetTemplates.find(
      template => template.identifier === this.widgetConstants.WIDGET_LABELS.WIDGET_BOOKING_INFO_APPOINTMENT
    );
    this.successMessage = this.utilService.getBookingInfoMessage(successTemplate, this.lang);

    if (!this.successMessage) {
      const translationConstant = (this.courtesyForm === 'Du' ? 'appointments.finalpage.successMessageDu' : 'appointments.finalpage.successMessageSie');
      this.translateService.get(translationConstant, { leadTime: this.leadTime }).subscribe({
        next: (res) => {
          this.successMessage = res;
          this.customEventService.finalSuccessMessageChangedEvent.emit(this.successMessage);
        }
      });
    } else {
      this.customEventService.finalSuccessMessageChangedEvent.emit(this.successMessage);
    }
  }

  // Init user and cart state from localstorage values
  private initFormState(): void {
    this.setInitStateLSName();
    this.setInitCartLSName();
    this.setInitUserLSName();
    this.setInitCustomFieldsLSName();

    this.customFieldService.addonsCustomFieldsPriceEvent.subscribe(() => {
      this.localStorageService.getItem(this.CUSTOMFIELDS_LS_NAME, (savedCustomFieldsState) => {
        if (savedCustomFieldsState) {
          this.customFieldsAddonsPrice = Object.values(savedCustomFieldsState as Record<string, { price?: number | number[] }>)
            .reduce((sum, item) =>
              sum + (item?.price
                ? Array.isArray(item.price)
                  ? item.price.reduce((a, b) => a + b, 0)
                  : item.price
                : 0),
              0);
          this.cartService.addonsCustomFieldsPrice = Number(this.customFieldsAddonsPrice);
          this.cartService.recalculateCart(this.cart);
          this.totalCartPrice = this.cartService.totalCartPrice;
          this.calculateCoupon();
          this.customEventService.addonsPriceChangeEvent.emit(this.customFieldsAddonsPrice);
        }
      });
    });
  }

  private setInitStateLSName(): void {
    // Read local storage to set form state
    this.localStorageService.getItem(this.STATE_LS_NAME, (savedFormState: any) => {
      if (savedFormState) {
        if (savedFormState?.customerProfileId) { this.appointmentState.customerProfileId = savedFormState.customerProfileId; }
        if (savedFormState?.store) { this.appointmentState.store = savedFormState.store; }
        if (savedFormState?.services) { this.appointmentState.services = savedFormState.services; }
        if (savedFormState?.worker) { this.appointmentState.worker = savedFormState.worker; }
        if (savedFormState?.date) { this.appointmentState.date = savedFormState.date; }
        if (savedFormState?.termins) { this.appointmentState.termins = savedFormState.termins; }
        if (savedFormState?.day) { this.appointmentState.day = savedFormState.day; }
        if (savedFormState?.default_worker_id) { this.appointmentState.default_worker_id = savedFormState.default_worker_id; }
        if (savedFormState?.leadGeneratorPostcode) { this.appointmentState.leadGeneratorPostcode = savedFormState.leadGeneratorPostcode; }
        if (savedFormState?.default_worker) { this.appointmentState.default_worker = savedFormState.default_worker; }
        if (savedFormState?.storeDefaultWorkerIds) { this.appointmentState.storeDefaultWorkerIds = savedFormState.storeDefaultWorkerIds; }

        if (this.appointmentState.date) {
          // If selected date from localstorage is in past ignore it
          if (this.appointmentState.date < this.now) {
            this.appointmentState.date = null;
          } else {
            // Set calendar default state to preselected month
            this.viewDate = this.appointmentState.date;
            this.lastCalendarMonth = this.viewDate.getMonth() + 1;
            this.updateCalendarPrevState(this.viewDate);
          }
        }
      }
    });
  }

  private setInitCartLSName(): void {
    this.localStorageService.getItem(this.CART_LS_NAME, (savedCartState: any) => {
      if (savedCartState) {
        this.cart = savedCartState;
      }
    });
  }

  private setInitUserLSName(): void {
    this.localStorageService.getItem(this.USER_LS_NAME, (savedUserState: any) => {
      if (savedUserState) {
        LoggerService.log('Saved user state from localstorage', savedUserState);
        this.userState = savedUserState;
      }
    });
  }

  private setInitCustomFieldsLSName(): void {
    this.localStorageService.getItem(this.CUSTOMFIELDS_LS_NAME, (savedCustomFieldsState: any) => {
      if (savedCustomFieldsState) {
        LoggerService.log('Saved customfields state from localstorage', savedCustomFieldsState);
        // Skip file-type answers — uploaded file tokens are consumed by the
        // previous booking and cannot be reused for a subsequent one.
        const filtered: any = {};
        Object.keys(savedCustomFieldsState).forEach((key) => {
          if (savedCustomFieldsState[key]?.type !== this.bookingCustomFieldTypes.FILE) {
            filtered[key] = savedCustomFieldsState[key];
          }
        });
        this.customFieldValues = filtered;
      }
    });
  }

  // To change state of previous button in calendar
  private updateCalendarPrevState(viewDate: Date): void {
    this.disablePrev = viewDate.getMonth() <= this.now.getMonth() && viewDate.getFullYear() <= this.now.getFullYear();
  }

  // The method called when you go to the calendar page
  loadCalendarWithAvailabilities({ body }: { body: CalendarMonthViewDay[] }) {
    const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();
    const nowTz = new Date(moment.tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
    let startDateContextMoment: moment.Moment;
    let endDateContextMoment: moment.Moment;
    let dayOfMonth: string;

    this.lastCalendarMonth = body[10].date.getMonth() + 1;
    this.exceptionDays = [];

    if (moment(body[10].date).isSame(nowTz, 'month')) {
      dayOfMonth = moment(nowTz).format(this.dateIsoFormat);
    } else {
      dayOfMonth = moment(body[10].date).tz(timezone).startOf('month').format(this.dateIsoFormat);
    }

    if (this.dateContext && this.dateContextStart && this.dateContextEnd) {
      startDateContextMoment = moment(this.dateContext, this.dateIsoFormat).tz(timezone).subtract(this.dateContextStart, 'days');
      endDateContextMoment = moment(this.dateContext, this.dateIsoFormat).tz(timezone).add(this.dateContextEnd, 'days');

      if (startDateContextMoment.isBefore(moment().tz(timezone))) {
        if (endDateContextMoment.isSameOrAfter(moment().tz(timezone))) {
          if (moment(body[10].date).isSame(nowTz, 'month')) {
            dayOfMonth = moment(nowTz).format(this.dateIsoFormat);
          } else {
            dayOfMonth = moment(body[10].date).tz(timezone).startOf('month').format(this.dateIsoFormat);
          }
        } else {
          dayOfMonth = moment(this.dateContext).tz(timezone).format(this.dateIsoFormat);
        }
      } else {
        const firstDateOfMonth = moment(body[10].date).tz(timezone).startOf('month');
        if (firstDateOfMonth.isAfter(startDateContextMoment)) {
          dayOfMonth = firstDateOfMonth.format(this.dateIsoFormat);
        } else {
          dayOfMonth = moment(this.dateContext).tz(timezone).format(this.dateIsoFormat);
        }
      }

      const endOfMonthMomentObj = moment(body[10].date).tz(timezone).endOf('month');
      if (endDateContextMoment.isBefore(endOfMonthMomentObj)) {
        this.customEventService.disableCalendarNextButtonEvent.emit(true);
      }
    }

    this.formService.getDaysWiseFreeSlots(
      this.worker.id,
      this.appointmentState.store,
      this.service.id.toString(),
      dayOfMonth,
      this.globals.isInternal,
      undefined,
      this.globals.debugMode,
      this.globals.internalToken,
      undefined,
      undefined,
      this.partner?.is_timezone_enabled ? this.effectiveDisplayTimezone : undefined,
      undefined
    ).pipe(
      takeWhile(() => this.alive)
    ).subscribe({
      next: availableSlots => {
        this.calendarDate = true;
        this.availableSlotsArray = [];
        let bookingDateIsAvailable = true;

        if (availableSlots && !('errors' in availableSlots)) {
          // preparing array that has day wise availabilites
          availableSlots.available_minutes_blocks?.forEach(daysWiseSlots => {
            daysWiseSlots.workers_minutes_blocks?.forEach(workerMinutesBlock => {
              workerMinutesBlock.minutes_blocks.forEach(availabilities => {
                availabilities.appointments_slots?.forEach(slot => {
                  const workerUuids = workerMinutesBlock.workers.map(worker => worker.uuid);
                  !this.availableSlotsArray[daysWiseSlots.date] && (this.availableSlotsArray[daysWiseSlots.date] = []);
                  this.availableSlotsArray[daysWiseSlots.date].push({
                    start: slot.appointment_start,
                    end: slot.appointment_end,
                    worker: workerMinutesBlock.worker,
                    workerUuids,
                    store: workerMinutesBlock.store,
                    startHour: parseInt(slot.appointment_start.split(' ')[1].split(':')[0], 10),
                    short_start: slot.appointment_short_start,
                    isReserved: slot.is_reserved
                  });
                });
              });
            });
          });

          bookingDateIsAvailable = (Object.keys(this.availableSlotsArray)?.length > 0);

          body.forEach(date => {
            const formatedDate = this.utilService.dateToStr(date.date);
            let dateDisabled = false;
            (this.days.length < 7) && this.days.push(date);

            if (
              !this.availableSlotsArray[formatedDate] || // date is not present in available dates array
              date.isPast || // if date is in past
              date.date > this.maxCalendarAvailabilityDate // date is bigger than max date
            ) {
              dateDisabled = true;
            }

            if (!dateDisabled && startDateContextMoment && endDateContextMoment) {
              if (moment(date.date).isBetween(startDateContextMoment, endDateContextMoment, undefined, '[]')) {
                if (!moment(date.date).isSameOrAfter(new Date(), 'day')) {
                  dateDisabled = true;
                }
              } else {
                dateDisabled = true;
              }
            }

            if (!dateDisabled && !this.dateContext && this.calendarPreselectedDate) {
              if ((date.date < moment(this.calendarPreselectedDate).toDate()) || (date.date > moment(this.calendarPreselectedDate).toDate())) {
                dateDisabled = true;
              }
            }

            if (dateDisabled) {
              date.cssClass = 'cal-disabled';
              date.isWeekend = true;
            }

            // date context is present and current date is same then preselect date
            if (!dateDisabled && this.appointmentState.date !== null && date.date.toDateString() === this.appointmentState.date.toDateString()) {
              this.onDateSelect({ day: date });
            }
          });

          if (!bookingDateIsAvailable) {
            const isAutoSearchEnabled = Number(this.partner.disable_automatic_search) !== 1;
            const shouldAutoLoad = !endDateContextMoment || moment.tz(timezone)
              .set('month', this.lastCalendarMonth)
              .startOf('month')
              .isBefore(endDateContextMoment);

            this.showBookableDateNotAvailableIssue = !isAutoSearchEnabled || !shouldAutoLoad;
            this.automaticBookableDateProcessIsEnabled = isAutoSearchEnabled && shouldAutoLoad;

            if (this.automaticBookableDateProcessIsEnabled) {
              this.autoLoadBookableDates();
            } else {
              // Reset calendar navigation flags
              LoggerService.log('[Reset flags of next and previous calendar button]');
              this.customEventService.eventCalendarIsNextPressed = undefined;
              this.customEventService.eventCalendarIsPreviousPressed = undefined;
            }
          }

          this.calendarLoaded = true;
        }
      },
      error: error => {
        // This subscribe had no error callback at all, so a failed request left
        // `calendarLoaded` false — the loader stays up and the month never renders.
        // Render the month grid instead so the arrows and the rest of the embed stay
        // usable. Known limits: with no slot data the days are NOT dimmed the way a
        // fully booked month is (clicking one is a no-op), and on a first-load
        // failure the weekday header is empty since `days` is only built on success.
        this.availableSlotsArray = [];
        this.calendarLoaded = true;
        LoggerService.error(error);
      }
    });
  }

  // Auto load next month availabilities when current month's doesn't have a single availability
  private autoLoadBookableDates() {
    LoggerService.log('Auto loading next month availabilities');

    const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();
    const nowInCurrentTz = moment.tz(timezone);

    const minDate = new Date(nowInCurrentTz.startOf('month').format(DATE_TIME_ISO_T_FORMAT));
    minDate.setHours(0, 0, 0, 0);

    const isNextPressed = this.customEventService.eventCalendarIsNextPressed;
    this.automaticBookableDateProcessIsEnabled = true;

    const currMonthDate = new Date(
      this.viewDate.getFullYear(),
      ([true, undefined].includes(isNextPressed) ? this.viewDate.getMonth() + 1 : this.viewDate.getMonth() - 1),
      1
    );

    if (currMonthDate >= minDate && currMonthDate < this.maxCalendarAvailabilityDate) {
      this.viewDate = currMonthDate;
      this.monthChanged = true;
      this.updateCalendarPrevState(this.viewDate);
      this.calendarLoaded = false;
    } else {
      this.calendarLoaded = true;
      this.automationBookingDatesLimitReached = true;

      // reset flags of next and previous calendar button
      LoggerService.log('[Reset flags of next and previous calendar button]');
      this.customEventService.eventCalendarIsNextPressed = undefined;
      this.customEventService.eventCalendarIsPreviousPressed = undefined;
    }
  }

  // Get slots when calendar day clicked
  onDateSelect({ day }: { day: CalendarMonthViewDay }): void | boolean {
    const formatedDate = this.utilService.dateToStr(day.date);

    if (day.isWeekend || day.isPast || day.date === this.appointmentState.date || !this.availableSlotsArray[formatedDate]) {
      LoggerService.warn('Selected date is not available due to may it is weekend or in past or disabled or already selected');
      return false;
    } else {
      this.toggleDateSelectedClass();
      this.appointmentState.day = day;
      this.appointmentState.date = day.date;
      this.freeAppointments = undefined;
      this.noAppointments = false;

      day.cssClass = 'cal-selected';
      day.badgeTotal = 0;

      if (this.oldSelectCalendarDate != null) {
        if (this.oldSelectCalendarDate.badgeTotal === 0) { this.oldSelectCalendarDate.cssClass = ''; }
      }

      if (!this.cartSupported) {
        this.cart = [];
        this.calendarSlotsCountList = [];
        if (this.oldSelectCalendarDate) {
          this.oldSelectCalendarDate.cssClass = '';
          this.oldSelectCalendarDate.badgeTotal = 0;
        }
      }

      this.oldSelectCalendarDate = day;
      this.showAvailableAppointments = true;
      this.showAppointmentSlotGhostElement = true;

      // trigger start event on first user interaction
      this.appointmentService.triggerCompactWidgetStartEvent();

      this.getFreeAppointments(formatedDate);
    }
  }

  // Setting up key names to store partner wise data into localstorage
  setLocalstorageNames(): void {
    LoggerService.log(this.partner);
    this.CART_LS_NAME = this.appointmentConstant.APPOINTMENT_CART_LS + this.partner.bookingName;
    this.USER_LS_NAME = this.appointmentConstant.APPOINTMENT_GLOBAL_USER_LS + this.partner.bookingName;
    this.CUSTOMFIELDS_LS_NAME = this.appointmentConstant.APPOINTMENT_CUSTOMFIELDS_LS_NAME + this.partner.bookingName;
    this.STATE_LS_NAME = this.appointmentConstant.APPOINTMENT_STATE_LS + this.partner.bookingName;
  }

  // On next prev month
  viewDateChanged(event: { viewDate: Date; isNext: boolean }): void {
    this.customEventService.eventCalendarIsNextPressed = event.isNext;
    this.customEventService.eventCalendarIsPreviousPressed = !event.isNext;

    this.freeAppointments = undefined;
    this.viewDate = event.viewDate;
    this.automaticBookableDateProcessIsEnabled = false;
    this.monthChanged = true;
    this.updateCalendarPrevState(event.viewDate);
    this.calendarLoaded = false;
    this.automationBookingDatesLimitReached = false;
    this.showAvailableAppointments = false;
  }

  private getFreeAppointments(date: string) {
    let freeAppointmentsSlots: AppointmentsSlotsDao[] = this.availableSlotsArray[date];
    this.appointmentService.resetSlotSettingsEvent.emit();
    this.freeAppointmentsCount = freeAppointmentsSlots.length;
    !freeAppointmentsSlots?.length && (this.noAppointments = true);
    this.showAppointmentSlotGhostElement = false;

    freeAppointmentsSlots = freeAppointmentsSlots.sort((a, b) => a.startHour - b.startHour);

    this.freeAppointments = {
      dayPeriod: undefined,
      appointments: freeAppointmentsSlots,
      showDayPeriodLabel: true,
      widgetGroup: undefined,
    };

    this.loadAppointmentCustomFields();
  }

  private toggleDateSelectedClass(): void {
    if (!this.cbcwWidgetWrapperRef.nativeElement.classList.contains('has-date-selected')) {
      this.cbcwWidgetWrapperRef.nativeElement.classList.add('has-date-selected');
    }
  }

  updateCart(eventData: {
    appointment: AppointmentsSlotsDao;
  }): void {
    this.addToCart(eventData.appointment);
  }

  private async addToCart(appointment: AppointmentsSlotsDao): Promise<void> {
    this.appointmentReservationError = false;
    // Reserve appointment if reservation feature is enabled
    const isReserved = await firstValueFrom(this.reserverAppointment(appointment));
    if (!isReserved) {
      return;
    }

    const appointmentStateDate = this.appointmentState.date;

    this.cartService.addToCart(
      appointment,
      this.appointmentState,
      [this.service],
      this.CART_LS_NAME,
      this.cart,
      false,
      true,
      [this.service.id],
      undefined,
      (cart: CartItem[]) => {
        this.cart = cart;
        this.cart.forEach(appointment => appointment.reservation_start_time = new Date());
        this.totalCartPrice = this.cartService.totalCartPrice;

        this.calendarSlotsCountList = [
          { date: appointmentStateDate, slotsCount: 1 },
        ];

        this.calculateCoupon();

        this.customEventService.cartItemsChangeEvent.emit({
          cart: this.cart,
          type: WIDGET_CONSTANTS.APPOINTMENT,
        });

        this.googleAnalyticsService.emitBookingEvent(
          GTM_EVENTS.COMPACT_WIDGET_BOOKING_STEP_AVAILABLE_APPOINTMENTS,
          this.prepareAnalyticsPayload()
        );

        this.currentView = this.appointmentConstant.PERSONAL_INFO;

        this.verifyOnlinePaymentStatus();
        this.resetDataBeforeLoadingSummaryPage();
      }
    );
  }

  private reserverAppointment(appointmentToReserve: AppointmentsSlotsDao): Observable<boolean> {
    if (Number(this.partner.is_appointment_reservation_enabled) === 1) {
      const appointment: AppointmentReservationBody = {
        appointment_service_id: this.service.id,
        worker_id: this.worker.id,
        start_time: appointmentToReserve.start,
        end_time: appointmentToReserve.end,
        ...this.partner?.is_timezone_enabled && { timezone: this.effectiveDisplayTimezone }
      };

      return this.formService.reserveAppointments([appointment])
        .pipe(
          switchMap(reservation => {
            if (!reservation?.success && reservation.preReserved) {
              this.appointmentReservationError = true;
              return of(false);
            }

            return of(true);
          }),
          catchError((error: HttpErrorResponse) => {
            LoggerService.error(error);
            return of(true);
          })
        );
    }

    return of(true);
  }

  removeAppointmentFromCart(cartItemId: string): void {
    if (this.adyenPaymentInFlight()) {
      // The payment running now was minted for this cart.
      return;
    }
    this.cartService.removeFromCart(
      cartItemId,
      this.CART_LS_NAME,
      this.cart,
      (cart: CartItem[]) => {
        this.removeReservedAppointments(this.cart[0]);
        this.cart = cart;
        this.appointmentService.resetSlotSettingsEvent.emit();
        this.currentView = this.appointmentConstant.APPOINTMENT;
      }
    );
  }

  private removeReservedAppointments(reservedAppointment: CartItem): void {
    if (Number(this.partner.is_appointment_reservation_enabled) === 1) {
      const appointment: AppointmentReservationBody = {
        appointment_service_id: reservedAppointment.servicesIds[0],
        worker_id: reservedAppointment.workerId,
        start_time: reservedAppointment.startTime,
        end_time: reservedAppointment.endTime,
        timezone: this.userState?.timezone
      };

      this.formService.removeReservedAppointment([appointment]).subscribe({
        next: reservation => {
          if (!reservation?.success) {
            LoggerService.warn('[Debug] Failed to remove a reserved appointment');
          }
        },
        error: (error: HttpErrorResponse) => {
          LoggerService.warn('[Debug] Failed to remove a reserved appointment');
          LoggerService.error(error);
        }
      });
    }
  }

  private loadAppointmentCustomFields(): void {
    this.isCustomFieldsLoaded = false;
    this.tempCustomFields = [];
    this.formService.getAppointmentCustomFields([this.service.id]).pipe(takeWhile(() => this.alive)).subscribe({
      next: (appointmentCustomFields) => {
        const trimmedArray: CustomFieldsDao[] = [];
        const values: number[] = [];

        if (!appointmentCustomFields) { appointmentCustomFields = []; }
        this.tempCustomFields = JSON.parse(JSON.stringify(appointmentCustomFields));

        // Translate any identifier-keyed prefill entries to their numeric custom-field id
        // once, before the id-keyed prefill loop below reads selectedJsonCustomFields.
        this.selectedJsonCustomFields = this.helperService.resolveCustomFieldIdentifierKeys(this.selectedJsonCustomFields, this.tempCustomFields);

        this.tempCustomFields.forEach(customField => {
          if (values.indexOf(customField.id) === -1 && !customField.parent_custom_field_id) {
            trimmedArray.push(customField);

            // Add default option if it is select field
            if ([this.bookingCustomFieldTypes.SELECT, this.bookingCustomFieldTypes.CARD_SELECT, this.bookingCustomFieldTypes.IMAGE_SELECT].includes(customField.type)) {
              if (
                customField.custom_field_options.length &&
                customField.custom_field_options[0]['id'] != null &&
                customField.type !== this.bookingCustomFieldTypes.CARD_SELECT &&
                customField.type !== this.bookingCustomFieldTypes.IMAGE_SELECT &&
                customField.type !== this.bookingCustomFieldTypes.SELECT
              ) {
                const tempCustomFieldOptions = new CustomFieldOptions();
                tempCustomFieldOptions.id = null;
                customField.custom_field_options.unshift(tempCustomFieldOptions);
              }

              if (!this.customFieldValues[customField.id]) {
                if (customField.is_multiple_select === 1) {
                  const passed = this.selectedJsonCustomFields && this.selectedJsonCustomFields[customField.id]
                    ? this.selectedJsonCustomFields[customField.id]
                    : undefined;
                  // passed is a comma-string on the querystring path, but the webcomponent's
                  // selected-custom-fields JSON can deliver an array/number for a multi-select —
                  // handle all three so .split() never throws "split is not a function".
                  const passedCustomFieldValue = passed
                    ? (Array.isArray(passed)
                      ? passed.map((value: any) => Number(value))
                      : String(passed).split(',').map((value: string) => Number(value)))
                    : [];

                  this.customFieldValues[customField.id] = {
                    value: passedCustomFieldValue,
                    type: customField.type,
                  };
                } else {
                  if (customField.type === this.bookingCustomFieldTypes.SELECT) {
                    if (this.selectedJsonCustomFields && this.selectedJsonCustomFields[customField.id]) {
                      this.customFieldValues[customField.id] = {
                        value: Number(this.selectedJsonCustomFields[customField.id]),
                        type: customField.type
                      };
                    } else if (this.customFieldValues[customField.id]) {
                      // Do nothing
                    } else {
                      this.customFieldValues[customField.id] = {
                        value: '',
                        type: customField.type,
                      };
                    }
                  } else if ([this.bookingCustomFieldTypes.CARD_SELECT, this.bookingCustomFieldTypes.IMAGE_SELECT].includes(customField.type)) {
                    if (this.selectedJsonCustomFields && this.selectedJsonCustomFields[customField.id]) {
                      this.customFieldValues[customField.id] = {
                        value: [Number(this.selectedJsonCustomFields[customField.id])],
                        type: customField.type
                      };
                    } else if (this.customFieldValues[customField.id]) {
                      // Do nothing
                    } else {
                      this.customFieldValues[customField.id] = {
                        value: [],
                        type: customField.type,
                      };
                    }
                  }
                }
              }
            } else if (customField.type === this.bookingCustomFieldTypes.NUMBER_SELECT) {
              const optionIds = customField.custom_field_options.map(item => item.id);
              const optionValues: {
                custom_field_option_id: number;
                custom_field_option_value: any;
              }[] = customField.custom_field_options.map(item => {
                item.option_value = item.number_default;
                return {
                  custom_field_option_id: item.id,
                  custom_field_option_value: item.number_default,
                };
              }
              );
              this.customFieldValues[customField.id] = {
                value: optionIds,
                option_values: optionValues,
                type: customField.type,
              };
            } else if (customField.type === this.bookingCustomFieldTypes.FILE) {
              this.customFieldValues[customField.id] = {
                type: this.bookingCustomFieldTypes.FILE,
                file_ids: this.customFieldValues[customField.id]?.file_ids?.length > 0
                  ? this.customFieldValues[customField.id]?.file_ids
                  : [],
              };
            } else if ([
              this.bookingCustomFieldTypes.TEXT,
              this.bookingCustomFieldTypes.TEXTAREA,
              this.bookingCustomFieldTypes.RADIO,
              this.bookingCustomFieldTypes.DATE]
              .includes(customField.type)) {
              if (this.selectedJsonCustomFields && this.selectedJsonCustomFields[customField.id]) {
                this.customFieldValues[customField.id] = this.selectedJsonCustomFields[customField.id];
              } else {
                this.customFieldValues[customField.id] = this.customFieldValues[customField.id]
                  ? this.customFieldValues[customField.id]
                  : undefined;
              }
            } else if (customField.type === this.bookingCustomFieldTypes.CHECKBOX) {
              if (this.selectedJsonCustomFields && this.selectedJsonCustomFields[customField.id]) {
                this.customFieldValues[customField.id] = ["true", "1", true, 1].includes(this.selectedJsonCustomFields[customField.id]) ? true : false;
              } else {
                this.customFieldValues[customField.id] = this.customFieldValues[customField.id]
                  ? ["true", "1", true, 1].includes(this.customFieldValues[customField.id]) ? true : false
                  : false;
              }
            }

            values.push(customField.id)
          } else {
            LoggerService.warn(`CALENSO_WIDGET: Custom field ${customField.id} is ignored!`);
          }
        });

        // Sort customfields by position field
        trimmedArray.sort((a, b) => a.position - b.position);

        this.appointmentCustomFields = trimmedArray;
        this.isCustomFieldsLoaded = true;

        LoggerService.log('customFieldValues', this.customFieldValues);
        LoggerService.log('appointmentCustomFields', this.appointmentCustomFields);
      },
      error: (error: HttpErrorResponse) => {
        this.isCustomFieldsLoaded = true;
        LoggerService.error(error);
      }
    });
  }

  switchLanguage(language: string): void {
    this.lang = language;
    this.translateService.use(language);
    this.globals.user_selected_language = language;
  }

  updateUserState(newUserState: UserState): void {
    this.userState = newUserState;
  }

  updateCustomerSelected(newCustomerSelected: object): void {
    this.customerSelected = newCustomerSelected;
  }

  private markFormGroupTouched(form: NgForm) {
    Object.values(form.controls).forEach((control) => {
      control.markAsTouched();
    });
  }

  private verifyOnlinePaymentStatus(): void {
    this.partnerService.verifyOnlinePaymentStatus().subscribe({
      next: (result) => {
        if (result) {
          this.isServiceOnlinePaymentEnabled = Number(result.isServiceOnlinePaymentEnabled);
          if (
            result.isServiceOnlinePaymentEnabled === 1 &&
            result.isStripeAppointmentPaymentEnabled === 1 &&
            result.stripeEnabled
          ) {
            this.isStripeEnabled = true;
          }
          if (result.isOnlinePaymentMandatory === 1) {
            this.isOnlinePaymentMandatory = true;
          }
          if (result.isBexioAppointmentServicePaymentEnabled === 1 && result.bexioEnabled) {
            this.isBexioEnabled = true;
          }
          if (result.isExternalInvoiceAppointmentPaymentEnabled === 1) {
            this.isExternalInvoiceEnabled = true;
            // see appointment.component.ts: the card renders on this flag, so a partner offering
            // only invoice would otherwise never be able to choose a payment type
            this.isServiceOnlinePaymentEnabled = 1;
          }
          if (result.isServiceOnlinePaymentEnabled === 1 && result.saferpayEnabled) {
            this.isSaferpayEnabled = true;
          }
          if (result.isServiceOnlinePaymentEnabled === 1 && result.adyenEnabled) {
            this.isAdyenEnabled = true;
          }
          if (result.isServiceOnlinePaymentEnabled === 1 && result.paypalEnabled
          ) {
            this.isPaypalEnabled = true;
            this.paypalSetting = new PayPalSettingsDbModel();
            this.paypalSetting.access_token = result.paypalAccessToken;
            this.paypalSetting.client_id = result.paypalClientId;
          }
          if (result.onlinePaymentShowOnSitePaymentType === 1 && result.isOnlinePaymentMandatory !== 1) {
            this.isStoreEnabled = true;
          }
          this.stripeConnectedAccountId = result.stripeConnectedAccountId ?? null;
          this.defaultPaymentType = result.defaultPaymentType ?? '';
        }
        this.paymentType = undefined;
      }
    });
  }

  private resetDataBeforeLoadingSummaryPage(): void {
    // Only reset feature flags — re-populated by verifyOnlinePaymentStatus().
    // Coupon and payment data are preserved so navigating back to summary
    // doesn't drop what the customer already applied.
    this.isStripeEnabled = false;
    this.isOnlinePaymentMandatory = false;
    this.isBexioEnabled = false;
    this.isExternalInvoiceEnabled = false;
    this.isStoreEnabled = false;
    this.isPaypalEnabled = false;
    this.isSaferpayEnabled = false;
    this.isAdyenEnabled = false;
  }

  private validatedPayment(): boolean {
    if (
      this.totalCartPrice === 0 ||
      this.totalCartPrice === -1 ||
      this.priceAfterCouponReduction <= 0
    ) {
      this.paymentType = this.paymentTypes.STORE;
      return true;
    } else {
      if (
        !this.isBexioEnabled &&
        !this.isStripeEnabled &&
        !this.isPaypalEnabled &&
        !this.isSaferpayEnabled &&
        !this.isAdyenEnabled &&
        !this.isExternalInvoiceEnabled
      ) {
        this.paymentType = this.paymentTypes.STORE;
        return true;
      } else {
        if (this.isOnlinePaymentMandatory) {
          if (
            (this.stripePaymentMethodId && this.paymentType === this.paymentTypes.CREDITCARD) ||
            (this.paymentType === this.paymentTypes.INVOICE) ||
            (this.paymentType === this.paymentTypes.EXTERNAL_INVOICE) ||
            (this.paypalOrderDetail && this.paymentType === this.paymentTypes.PAYPAL) ||
            (this.saferPayPaymentDetails && this.paymentType === this.paymentTypes.SAFERPAY) ||
            (this.adyenPaymentDetails && this.paymentType === this.paymentTypes.ADYEN)
          ) {
            return true;
          } else {
            this.customEventService.paymentGatewayErrorEvent.emit(true);
            return false;
          }
        } else {
          if (this.paymentType === this.paymentTypes.CREDITCARD) {
            if (this.stripePaymentMethodId) {
              return true;
            } else {
              return false;
            }
          } else if (this.paymentType === this.paymentTypes.INVOICE) {
            return true;
          } else if (this.paymentType === this.paymentTypes.EXTERNAL_INVOICE) {
            return true;
          } else if (this.paymentType === this.paymentTypes.STORE) {
            return true;
          } else if (this.paymentType === this.paymentTypes.SAFERPAY) {
            if (this.saferPayPaymentDetails) {
              return true;
            } else {
              this.customEventService.paymentGatewayErrorEvent.emit(true);
              return false;
            }
          } else if (this.paymentType === this.paymentTypes.PAYPAL) {
            if (this.paypalOrderDetail) {
              return true;
            } else {
              this.customEventService.paymentGatewayErrorEvent.emit(true);
              return false;
            }
          } else if (this.paymentType === this.paymentTypes.ADYEN) {
            if (this.adyenPaymentDetails) {
              return true;
            } else {
              this.customEventService.paymentGatewayErrorEvent.emit(true);
              return false;
            }
          } else {
            this.customEventService.paymentGatewayErrorEvent.emit(true);
            return false;
          }
        }
      }
    }
  }

  protected validateAndBooking(): boolean {
    this.widgetUtilService.customerProfile = undefined;
    if (this.formService.imageUploaded === false) {
      LoggerService.warn('[Processing] Image is uploading...');
      this.markFormGroupTouched(this.appointmentForm);
      this.validatedPayment();
      return false;
    }

    if (!this.cart?.length) {
      LoggerService.warn('[Validation] Cart is empty');
      this.markFormGroupTouched(this.appointmentForm);
      this.validatedPayment();
      return false;
    }

    if (this.appointmentForm.invalid) {
      this.widgetUtilService.handleFormInvalidState(this.appointmentForm, this.elementRef);
      this.validatedPayment();
      return false;
    }

    if (Number(this.partner.is_captcha_feature_enabled) === 1) {
      if (this.partner.captcha_type === CAPTCHA_TYPE.CALENSO_DEFAULT && !this.userState.captcha_challenge) {
        LoggerService.warn('[Validation failed] Calenso DefaultCaptcha is required');
        this.markFormGroupTouched(this.appointmentForm);
        this.validatedPayment();
        return false;
      } else if (this.partner.captcha_type === CAPTCHA_TYPE.FRIENDLY && !this.userState.friendly_captcha_challenge) {
        LoggerService.warn('[Validation failed] Friendly Captcha is required');
        this.captchaService.friendlyCaptchaValidationRequired.emit(true);
        this.markFormGroupTouched(this.appointmentForm);
        this.validatedPayment();
        return false;
      }
    }

    /*
    * Case1: Current view is personal info then check if cart price is > 0 and payment is enabled
    * then navigate to payment screen otherwise procced for booking
    * Case2: Current view is personal info and cart price is <= 0 then procced for booking
    * Case3: Current view is payment then check all the open amount must be paid and then procced for booking
    */
    if (this.currentView === this.appointmentConstant.PERSONAL_INFO) {
      if (this.totalCartPrice > 0 && this.isServiceOnlinePaymentEnabled === 1) {
        this.currentView = this.appointmentConstant.PAYMENT;
        this.googleAnalyticsService.emitBookingEvent(
          GTM_EVENTS.COMPACT_WIDGET_BOOKING_STEP_PAYMENT,
          this.prepareAnalyticsPayload()
        );
        return false;
      } else {
        this.doBooking();
        return true;
      }
    } else if (this.currentView === this.appointmentConstant.PAYMENT) {
      if (this.validatedPayment()) {
        this.doBooking();
        return true;
      } else {
        return false;
      }
    } else {
      LoggerService.warn('[Validation] Something went wrong');
    }
  }

  onChangePaymentType(event: string): void {
    this.paymentType = event;
    this.stripePaymentMethodId = null;
    this.paypalOrderDetail = undefined;
    this.saferPayPaymentDetails = undefined;
    this.adyenPaymentDetails = undefined;
    this.isPaymentInProgress = false;
    this.invoiceAddressChecked = false;
    this.invoiceAddressValid = true;
    this.invoiceAddress = undefined;
    if (event === this.paymentTypes.CREDITCARD && this.isStripeEnabled && (this.priceAfterCouponReduction ?? this.totalCartPrice) > 0) {
      this.disableBookingButton = true;
      this.stripeElementLoading = true;
    } else if (!this.isReservationExpired) {
      this.disableBookingButton = false;
      this.stripeElementLoading = false;
    }
  }

  onCouponSuccessEvent(event: CouponDbModel): void {
    this.coupon = event;
    this.couponDisableConfirmButton = false;
    this.isInvalidCoupon = false;
    this.calculateCoupon();
    // A price of 0 unmounts the Stripe element before it can report ready, so the
    // loading gate must be released here or the book button stays disabled forever.
    if (this.priceAfterCouponReduction <= 0) {
      this.disableBookingButton = false;
      this.stripeElementLoading = false;
    }
    this.paypalOrderDetail = undefined;
    this.customEventService.couponChangeEvent.emit(this.finalCouponDiscount);
  }

  /**
   * Standalone "Invoice" payment mode: keeps the booking button disabled while the
   * "different address" checkbox is checked but the invoice-address form is incomplete.
   */
  protected readonly isInvoiceAddressMandatory = isInvoiceAddressMandatoryFor;

  onInvoiceAddressChangeEvent(event: { checked: boolean; valid: boolean; value: AppointmentBookItem['invoice_address'] | null }): void {
    this.invoiceAddressChecked = event.checked;
    this.invoiceAddressValid = event.valid;
    this.invoiceAddress = event.value ?? undefined;
  }

  get isInvoiceAddressFormBlockingBooking(): boolean {
    return this.invoiceAddressChecked && !this.invoiceAddressValid;
  }

  onCouponFailedEvent(event: {
    disableBookingButton: boolean;
    invalidCoupon: boolean;
  }): void {
    this.coupon = undefined;
    this.finalCouponDiscount = 0;
    this.priceAfterCouponReduction = undefined;
    this.couponDisableConfirmButton = event.disableBookingButton;
    this.isInvalidCoupon = event.invalidCoupon;
    this.calculateCoupon();
    this.paypalOrderDetail = undefined;
  }

  calculateCoupon(): void {
    if (this.coupon && this.coupon.is_fixed_amount === 1) {
      this.finalCouponDiscount = Number(this.coupon.fixed_amount);
    }

    if (this.coupon && this.coupon.is_percentage === 1) {
      this.finalCouponDiscount = Number(((Number(this.totalCartPrice) / 100) * this.coupon.percentage).toFixed(2));
    }

    const netAfterCoupon = Number(this.totalCartPrice) - Number(this.finalCouponDiscount);
    const vat = this.calculateCartVat(netAfterCoupon);
    this.cartVat.set(vat);
    // priceAfterCouponReduction carries the GROSS amount — it is what cw-payment-methods and the
    // Stripe PaymentIntent use as the amount to charge.
    this.priceAfterCouponReduction = Number((netAfterCoupon + vat.totalCents / 100).toFixed(2));
  }

  // Summed in whole cents: 100.10 + 7.60 in floating point is 107.69999999999999, which a
  // payment provider that truncates would charge as 107.69.
  protected cartTotalInclVat(): number {
    if (!(Number(this.totalCartPrice) > 0)) {
      return this.totalCartPrice;
    }
    return (Math.round(Number(this.totalCartPrice) * 100) + this.cartVat().totalCents) / 100;
  }

  /**
   * VAT already contained in the cart for `inclusive` partners, on the price after the coupon —
   * display only, recomputed from the restored state so it survives a payment return.
   */
  protected containedVat(): VatResult {
    if (vatTypeLabelKey(this.partner?.widget_vat_type) !== 'inclusive') {
      return { byRate: [], totalCents: 0 };
    }
    const discountedNetCents = Math.max(0, Math.round((Number(this.totalCartPrice) - Number(this.finalCouponDiscount || 0)) * 100));
    const undiscountedNetCents = Math.round(Number(this.totalCartPrice) * 100);
    return calculateIncludedVat(this.cartService.totalCartTaxableLines, discountedNetCents, undiscountedNetCents);
  }

  /** Net-after-coupon → VAT (empty/zero for `inclusive` partners — see D1 in the plan). */
  private calculateCartVat(discountedNet: number): VatResult {
    if (!isVatAddedOnTop(this.partner?.widget_vat_type)) {
      return { byRate: [], totalCents: 0 };
    }
    const discountedNetCents = Math.round(discountedNet * 100);
    const undiscountedNetCents = Math.round(Number(this.totalCartPrice) * 100);
    return calculateVat(this.cartService.totalCartTaxableLines, discountedNetCents, undiscountedNetCents);
  }

  /** Per-item `+ amount (plus X% VAT)` row — ratio 1 (no cross-item coupon allocation here). */
  getItemVatAmount(taxableLines: { netCents: number; rateHundredths: number }[], itemNet: number): number {
    if (!isVatAddedOnTop(this.partner?.widget_vat_type) || !(itemNet > 0)) {
      return 0;
    }
    const itemNetCents = Math.round(itemNet * 100);
    const result = calculateVat(taxableLines || [], itemNetCents, itemNetCents);
    return result.totalCents / 100;
  }

  onStripeSuccessfulEvent(method: string) {
    this.stripePaymentMethodId = method;
    this.validateAndBooking();
  }

  onResetStripeTokenEvent(errorMessage: any) {
    this.bookingErrorMessage = errorMessage;
    this.helperService.bookingLoader.emit(false);
    if (!this.isReservationExpired) {
      this.disableBookingButton = false;
    }
    this.stripeElementLoading = false;
    this.bookButtonLoader = false;
    this.stripePaymentMethodId = null;
    this.storageComplete = false;
  }

  onStoreBookingItemsEvent(): void {
    // Prepare and store data when triggered from Stripe component (redirect / 3DS flow)
    if (this.paymentType === this.paymentTypes.CREDITCARD && !this.stripePaymentMethodId) {
      LoggerService.log('[locale Storage] Storing booking data before Stripe verification (Compact booking; triggered from Stripe component)');

      // Booking items are prepared BEFORE storing - the same items are booked after the redirect.
      const data = this.buildRedirectionSnapshot();

      this.utilService.updateLocalStorage(APPOINTMENT_CONSTANT.APPOINTMENT_STRIPE_REDIRECTION_DATA_LS_NAME, data);
      this.storageComplete = true;
    }
  }

  /** The booking as it stands, for a payment return (Stripe redirect, Adyen resume) to book as is. */
  private buildRedirectionSnapshot(): Record<string, unknown> {
    return {
      bookingItems: this.prepareBookingItems(),
      appointmentState: this.appointmentState,
      cart: this.cart,
      customFieldValues: this.customFieldValues,
      userState: this.userState,
      totalCartPrice: this.totalCartPrice,
      cartVat: this.cartVat(),
      coupon: this.coupon,
      finalCouponDiscount: this.finalCouponDiscount,
      priceAfterCouponReduction: this.priceAfterCouponReduction,
      isInvalidCoupon: this.isInvalidCoupon,
      currentView: this.currentView,
      service: this.service,
      store: this.store,
      tempCustomFields: this.tempCustomFields,
      appointmentCustomFields: this.appointmentCustomFields,
      isServiceOnlinePaymentEnabled: this.isServiceOnlinePaymentEnabled,
      isStripeEnabled: this.isStripeEnabled,
      isOnlinePaymentMandatory: this.isOnlinePaymentMandatory,
      isBexioEnabled: this.isBexioEnabled,
      isExternalInvoiceEnabled: this.isExternalInvoiceEnabled,
      isStoreEnabled: this.isStoreEnabled,
      isPaypalEnabled: this.isPaypalEnabled,
      isSaferpayEnabled: this.isSaferpayEnabled,
      stripeConnectedAccountId: this.stripeConnectedAccountId,
      defaultPaymentType: this.defaultPaymentType,
      isAdyenEnabled: this.isAdyenEnabled,
    };
  }

  /** Puts back the booking a payment return parked (see buildRedirectionSnapshot). */
  private restoreRedirectionSnapshot(data: Record<string, any>): void {
    // Restore local state for a consistent UX
    this.appointmentState = data.appointmentState ?? this.appointmentState;
    this.cart = data.cart ?? this.cart;
    this.customFieldValues = data.customFieldValues ?? this.customFieldValues;
    this.userState = data.userState ?? this.userState;
    this.totalCartPrice = data.totalCartPrice ?? this.totalCartPrice;
    if (data.cartVat) {
      this.cartVat.set(data.cartVat);
    }
    this.coupon = data.coupon ?? this.coupon;
    this.finalCouponDiscount = data.finalCouponDiscount ?? this.finalCouponDiscount;
    this.priceAfterCouponReduction = data.priceAfterCouponReduction ?? this.priceAfterCouponReduction;
    this.isInvalidCoupon = data.isInvalidCoupon ?? this.isInvalidCoupon;
    this.currentView = data.currentView ?? this.currentView;

    // Restore service and store data needed for booking
    this.service = data.service ?? this.service;
    this.store = data.store ?? this.store;
    this.tempCustomFields = data.tempCustomFields ?? this.tempCustomFields;
    this.appointmentCustomFields = data.appointmentCustomFields ?? this.appointmentCustomFields;

    // Restore payment flags
    this.isServiceOnlinePaymentEnabled = data.isServiceOnlinePaymentEnabled ?? this.isServiceOnlinePaymentEnabled;
    this.isStripeEnabled = data.isStripeEnabled ?? this.isStripeEnabled;
    this.isOnlinePaymentMandatory = data.isOnlinePaymentMandatory ?? this.isOnlinePaymentMandatory;
    this.isBexioEnabled = data.isBexioEnabled ?? this.isBexioEnabled;
    this.isExternalInvoiceEnabled = data.isExternalInvoiceEnabled ?? this.isExternalInvoiceEnabled;
    this.isStoreEnabled = data.isStoreEnabled ?? this.isStoreEnabled;
    this.isPaypalEnabled = data.isPaypalEnabled ?? this.isPaypalEnabled;
    this.isSaferpayEnabled = data.isSaferpayEnabled ?? this.isSaferpayEnabled;
    this.stripeConnectedAccountId = data.stripeConnectedAccountId ?? this.stripeConnectedAccountId;
    this.defaultPaymentType = data.defaultPaymentType ?? this.defaultPaymentType;
    this.isAdyenEnabled = data.isAdyenEnabled ?? this.isAdyenEnabled;

    // Mark service and custom fields as loaded since we restored them
    this.loadingService = false;
    this.isCustomFieldsLoaded = true;
  }

  /**
   * The shopper comes back from a page an Adyen payment had to leave for (a 3DS redirect, or the
   * hosted page in this tab). Restores the booking exactly as it was parked, lets AdyenResumeService
   * finish the payment, and books once -
   * the same stored booking a Stripe redirect return books, with the Adyen handles in place of the
   * payment intent. `/book` still verifies the payment with Adyen itself.
   */
  private resumeAdyenPayment(resume: AdyenResumeReturn): void {
    const data = resume.record.snapshot.data as Record<string, any>;
    if (!Array.isArray(data?.bookingItems)) {
      this.adyenResumeService.clear(resume.record.partnerId);
      return;
    }
    this.partner && this.setLocalstorageNames();
    this.restoreRedirectionSnapshot(data);
    this.disableBookingButton = true;
    this.bookButtonLoader = true;
    this.bookingErrorMessage = undefined;
    this.showStripeRedirectionLoader = true;
    this.paymentType = this.paymentTypes.ADYEN;

    this.adyenResumeService.complete(resume, {
      holdScope: buildAdyenHoldScope({
        widgetType: this.widgetConstants.COMPACT,
        cart: this.cart,
        storeId: this.appointmentState?.store,
      }),
      items: this.cart?.length ?? 0,
    }).then(details => {
      this.adyenPaymentDetails = details;
      this.processBooking((data.bookingItems as AppointmentBookItem[]).map(item => (
        item.payment_type === this.paymentTypes.ADYEN
          ? { ...item, adyen_session_id: details.session_id, adyen_session_result: details.session_result }
          : item
      )));
    }, (failure: AdyenResumeFailure) => {
      // The booking stays restored on the payment step; paying again mints a fresh session unless
      // the payment is held (Pending), which cw-adyen then says itself.
      this.showStripeRedirectionLoader = false;
      this.disableBookingButton = false;
      this.bookButtonLoader = false;
      this.bookingErrorMessage = failure?.silent
        ? undefined
        : this.translateService.instant(failure?.messageKey ?? 'adyen.errors.unexpected');
    });
  }

  onStripElementLoadEvent(isStripeLoaded: boolean) {
    // Loaded or failed, the loading gate must release — a failed load surfaces its
    // error through resetStripeTokenEvent and must not leave the button dead.
    this.stripeElementLoading = false;
    if (!this.isReservationExpired) {
      this.disableBookingButton = false;
    }
  }

  /**
   * Adyen authorised - the one entry point for every Adyen result, so one result can start at most
   * one booking. No Book press drives the payment here: Drop-in has its own pay button and the
   * hosted page its own, so the shopper has been charged with nothing booked. Book now, exactly as a
   * press would, unless something would stop that press: then the payment stays shown as done and
   * the shopper completes the rest and presses Book.
   */
  protected onAdyenPaid(details: AdyenPaymentDetails): void {
    this.adyenPaymentInFlight.set(false);
    this.adyenPaymentDetails = details;
    if (this.isReservationExpired) {
      // Paid after the reservation ran out (a 3DS challenge or the hosted page can outlast it):
      // nothing books it, so say what happens to the money - the backend refunds it unbooked.
      this.bookingErrorMessage = this.translateService.instant(reservationExpiredMessageKey(this.isAdyenPaymentHeld()));
    } else if (this.canBookOnItsOwn()) {
      this.submitBooking();
    }
  }

  /** Drop-in or the hosted window really started a payment: lock the booking until it reports back. */
  protected onAdyenPaymentStarted(): void {
    this.adyenPaymentInFlight.set(true);
  }

  /** Refused, cancelled, closed without paying, or an SDK error: the booking unlocks. */
  protected onAdyenReset(): void {
    this.adyenPaymentInFlight.set(false);
    this.adyenPaymentDetails = undefined;
    if (this.isReservationExpired) {
      // No payment is held any more, so the refund wording no longer applies.
      this.bookingErrorMessage = this.translateService.instant(reservationExpiredMessageKey(false));
    }
  }

  /**
   * Would a Book press go through now? A booking already on its way disables Book, so a result
   * reported twice cannot send a second /book.
   */
  private canBookOnItsOwn(): boolean {
    return canBookAfterAdyenPayment({
      paymentHeld: this.isAdyenPaymentHeld(),
      bookingBlocked: this.disableBookingButton
        || this.bookButtonLoader
        || this.isInvoiceAddressFormBlockingBooking
        || this.payAgainRequired(),
      reservationExpired: this.isReservationExpired,
      formIncomplete: this.formService.imageUploaded === false
        || !this.cart?.length
        || !!this.appointmentForm?.invalid,
      captchaPending: isCaptchaChallengePending(this.partner, this.userState),
    });
  }

  /** An Adyen payment is held for this booking: paid, not booked yet. */
  private isAdyenPaymentHeld(): boolean {
    return this.paymentType === this.paymentTypes.ADYEN && !!this.adyenPaymentDetails;
  }

  onReservationExpired(expired: boolean): void {
    const wasExpired = this.isReservationExpired;
    this.isReservationExpired = expired;
    if (expired) {
      this.disableBookingButton = true;
      if (this.bookingRequestInFlight) {
        // A /book is already on its way - a paid one may be waiting out the booking lock - and it may
        // well book. Its answer decides what the shopper is told: no "nothing was booked, refunded"
        // beside it.
        return;
      }
      // With a payment held for this booking, going back would drop it: say that it is refunded.
      this.bookingErrorMessage = this.translateService.instant(reservationExpiredMessageKey(this.isAdyenPaymentHeld()));
    } else if (wasExpired && !this.bookButtonLoader) {
      // A refreshed reservation lifts the block; only undo what the expiry set,
      // and never while a booking request is still in flight.
      this.disableBookingButton = false;
      this.bookingErrorMessage = undefined;
    }
  }

  onPaypalSuccessfulEvent(event: PayPalOrderDetailModel) {
    this.paypalOrderDetail = event;
    this.doBooking();
  }

  onResetPaypalEvent(event: any) {
    this.paypalOrderDetail = null;
    this.isPaymentInProgress = false;
  }

  onCancelPayment(): void {
    this.paypalOrderDetail = undefined;
    this.isPaymentInProgress = false;
    this.customEventService.resetPayPalOrderDetailEvent.emit();
  }

  payUsingSaferpay(): void {
    if (this.appointmentForm.valid && this.formService.imageUploaded === true) {
      this.showFullscreenOverlay = true;
      this.paymentService.initiateSaferpaySubject.next(true);
    } else {
      this.markFormGroupTouched(this.appointmentForm);
      this.validatedPayment();
    }
  }

  payUsingStripe(): void {
    if (this.stripePaymentMethodId) {
      this.validateAndBooking();
      return;
    }
    if (this.appointmentForm.valid && this.formService.imageUploaded === true) {
      this.disableBookingButton = true;
      this.bookButtonLoader = true;
      this.paymentService.initiateStripeSubject.next(true);
    } else {
      this.markFormGroupTouched(this.appointmentForm);
      this.validatedPayment();
    }
  }

  closeSaferpayOverlay(): void {
    this.showFullscreenOverlay = false;
    this.onResetSaferpayEvent('');
    this.paymentService.handleSaferpayBackgroundTriggerManually.next(true);
  }

  onSaferpaySuccessfulEvent(paymentDetails: SaferPayPaymentDetailsDbModel) {
    this.saferPayPaymentDetails = paymentDetails;
    this.showFullscreenOverlay = false;
    this.doBooking();
  }

  onResetSaferpayEvent(errorMessage: string): void {
    this.bookingErrorMessage = errorMessage;
    this.disableBookingButton = false;
    this.bookButtonLoader = false;

    this.saferPayPaymentDetails = undefined;
    this.showFullscreenOverlay = false;
  }

  private prepareBookingItems(): AppointmentBookItem[] {
    const bookingItems: AppointmentBookItem[] = [];
    const customFieldValues: {
      custom_field_id: number;
      type: string;
      value?: any;
      file_ids?: any;
      option_values?: {
        custom_field_option_id: number;
        custom_field_option_value: string;
      }[];
    }[] = [];
    const rawCustomFieldIds = this.tempCustomFields.map(i => i.id);

    let fieldIds = Object.keys(this.customFieldValues);
    if (rawCustomFieldIds?.length > 0) {
      fieldIds = fieldIds.filter(id => rawCustomFieldIds.indexOf(Number(id)) > -1);
    }

    for (let i = 0; i < fieldIds.length; i++) {
      if (this.customFieldValues[fieldIds[i]]?.type === this.bookingCustomFieldTypes.SELECT) {
        if (Array.isArray(this.customFieldValues[fieldIds[i]]?.value)) {
          customFieldValues.push({
            custom_field_id: Number(fieldIds[i]),
            type: this.bookingCustomFieldTypes.SELECT,
            value: this.customFieldValues[fieldIds[i]]?.value,
          });
        } else {
          customFieldValues.push({
            custom_field_id: Number(fieldIds[i]),
            type: this.bookingCustomFieldTypes.SELECT,
            value: [this.customFieldValues[fieldIds[i]]?.value],
          });
        }
      } else if (this.customFieldValues[fieldIds[i]]?.type === this.bookingCustomFieldTypes.CARD_SELECT) {
        customFieldValues.push({
          custom_field_id: Number(fieldIds[i]),
          type: this.bookingCustomFieldTypes.CARD_SELECT,
          value: this.customFieldValues[fieldIds[i]]?.value,
        });
      } else if (this.customFieldValues[fieldIds[i]]?.type === this.bookingCustomFieldTypes.IMAGE_SELECT) {
        customFieldValues.push({
          custom_field_id: Number(fieldIds[i]),
          type: this.bookingCustomFieldTypes.IMAGE_SELECT,
          value: this.customFieldValues[fieldIds[i]]?.value,
        });
      } else if (
        this.customFieldValues[fieldIds[i]]?.type === this.bookingCustomFieldTypes.NUMBER_SELECT
      ) {
        customFieldValues.push({
          custom_field_id: Number(fieldIds[i]),
          type: this.bookingCustomFieldTypes.NUMBER_SELECT,
          value: this.customFieldValues[fieldIds[i]]?.value,
          option_values: this.customFieldValues[fieldIds[i]]?.option_values,
        });
      } else if (this.customFieldValues[fieldIds[i]]?.type === this.bookingCustomFieldTypes.FILE) {
        customFieldValues.push({
          custom_field_id: Number(fieldIds[i]),
          type: this.bookingCustomFieldTypes.FILE,
          file_ids: Object.assign(
            [],
            this.customFieldValues[fieldIds[i]]?.file_ids
          ),
        });
      } else {
        let tempCustomFieldsDao: CustomFieldsDao;
        if (this.tempCustomFields?.length > 0) {
          tempCustomFieldsDao = this.tempCustomFields.find((item) => {
            return Number(item.id) === Number(fieldIds[i]);
          });
        }
        customFieldValues.push({
          custom_field_id: Number(fieldIds[i]),
          type: tempCustomFieldsDao ? tempCustomFieldsDao?.type : '',
          value: this.customFieldValues[fieldIds[i]]?.toString(),
        });
      }
    }

    // Ensure hidden custom fields are included with proper empty values
    const hiddenSelectTypes = [
      this.bookingCustomFieldTypes.SELECT,
      this.bookingCustomFieldTypes.CARD_SELECT,
      this.bookingCustomFieldTypes.IMAGE_SELECT,
      this.bookingCustomFieldTypes.NUMBER_SELECT,
      this.bookingCustomFieldTypes.WORKER_SELECT,
    ];
    for (const hiddenField of this.tempCustomFields.filter(cf => cf.is_hidden === 1)) {
      if (hiddenField.type === this.bookingCustomFieldTypes.FILE) {
        continue;
      }
      const existingIdx = customFieldValues.findIndex(cf => cf.custom_field_id === hiddenField.id);
      if (existingIdx > -1) {
        const entry = customFieldValues[existingIdx];
        if (entry.value === undefined || entry.value === null) {
          entry.value = hiddenSelectTypes.includes(hiddenField.type) ? [] : '';
        } else if (Array.isArray(entry.value) && entry.value.includes(null)) {
          entry.value = entry.value.filter((v: number | null) => v !== null);
        }
      } else {
        customFieldValues.push({
          custom_field_id: Number(hiddenField.id),
          type: hiddenField.type,
          value: hiddenSelectTypes.includes(hiddenField.type) ? [] : '',
        });
      }
    }

    LoggerService.log('Final custom field values', customFieldValues);

    for (const i in this.cart) {
      if (this.cart.hasOwnProperty(i)) {
        const bookingData: AppointmentBookItem = {
          amount: (this.cart[i].total.price === -1 ? -1 : this.cart.reduce((acc, item) => acc + item.total.price, 0) + this.customFieldsAddonsPrice),
          store_id: this.cart[i].storeId,
          store_name: this.cart[i].storeName,
          partnerId: this.partner['id'],
          services: this.cart[i]['servicesIds'].join(),
          referenceServices: this.cart[i]['referenceServices'],
          worker_id: this.cart[i]['workerId'],
          worker_email: this.cart[i].workerEmail,
          worker_uuids: this.cart[i].workerUuids,
          resource_name: this.cart[i].workerName,
          start: this.cart[i]['startTime'],
          end: this.cart[i]['endTime'],
          title: this.userState.title,
          prename: this.userState.forename,
          gender: this.userState.gender,
          lastname: this.userState.lastName,
          email: this.userState.eMail,
          phone: this.userState.phone,
          mobile: this.userState.mobile,
          birthday: this.userState?.birthday ? moment(this.userState?.birthday).format(DATE_ISO_FORMAT) : undefined,
          comment: this.userState.comment,
          customer_id: this.userState.customerId,
          customer_uuid: this.userState.customerUuid,
          country: this.userState.country_id,
          company_name: (this.userState?.company || undefined),
          internal_comment: (this.userState?.internal_comment || undefined),
          captcha: (this.userState?.captcha_challenge || undefined),
          friendly_captcha: (this.userState?.friendly_captcha_challenge || undefined),
          token: (this.appointmentState?.captcha?.uuid || undefined),
          customer_notification_preference: this.userState.customer_notification_preference,
          meeting_type_id: this.userState.meeting_type_id,
          customFieldValues: customFieldValues,
          user_browser_language: this.globals.user_browser_language,
          user_selected_language: this.globals.user_selected_language,
          coupon_code: this.coupon && !this.isInvalidCoupon ? this.coupon.code : null,
          payment_type: this.paymentType,
          ...(this.paymentType === this.paymentTypes.EXTERNAL_INVOICE && this.invoiceAddressChecked && this.invoiceAddress
            ? { invoice_address: this.invoiceAddress }
            : {}),
          booker_worker_id: this.bookerWorkerId,
          appointment_source_id: this.globals?.isInternal ? APPOINTMENT_SOURCE_ID.INTERNAL_WIDGET : APPOINTMENT_SOURCE_ID.EXTERNAL_WIDGET,
          utm_source: ((this.utmSource && this.utmSource !== 'null') ? this.utmSource : undefined),
          utm_content: ((this.utmContent && this.utmContent !== 'null') ? this.utmContent : undefined),
          utm_medium: ((this.utmMedium && this.utmMedium !== 'null') ? this.utmMedium : undefined),
          utm_campaign: ((this.utmCampaign && this.utmCampaign !== 'null') ? this.utmCampaign : undefined),
          utm_term: ((this.utmTerm && this.utmTerm !== 'null') ? this.utmTerm : undefined),
          utm_id: ((this.utmId && this.utmId !== 'null') ? this.utmId : undefined),
          is_internal: this.globals?.isInternal ? true : false,
          ...(this.userState.acceptDataPolicy !== undefined) && { accept_data_policy: +this.userState.acceptDataPolicy },
          ...(this.partner?.show_address_on_widget !== 0 ? {
            street: this.userState.street,
            zip: this.userState.zip,
            city: this.userState.city
          } : {}),
          ...this.partner?.is_timezone_enabled && { timezone: this.effectiveDisplayTimezone },
        };

        if (this.paymentType === this.paymentTypes.PAYPAL) {
          bookingData.paypal_order_id = this.paypalOrderDetail.paypal_order_id;
          bookingData.paypal_payer_id = this.paypalOrderDetail.paypal_payer_id;
          bookingData.paypal_status = this.paypalOrderDetail.paypal_status;
          bookingData.paypal_raw_data = this.paypalOrderDetail.paypal_raw_data;
          bookingData.paypal_capture_id = this.paypalOrderDetail.paypal_capture_id;
          bookingData.paypal_payment_log_id = this.paypalOrderDetail.paypal_payment_log_id;
        } else if (this.paymentType === this.paymentTypes.SAFERPAY) {
          bookingData.saferpay_transaction_id = this.saferPayPaymentDetails.transaction_id;
          bookingData.saferpay_payment_method = this.saferPayPaymentDetails.payment_method;
        } else if (this.paymentType === this.paymentTypes.ADYEN) {
          // Opaque handles only. The backend re-reads the session from Adyen
          // and believes that, never what the browser says was paid.
          bookingData.adyen_session_id = this.adyenPaymentDetails?.session_id;
          bookingData.adyen_session_result = this.adyenPaymentDetails?.session_result;
        } else if (this.paymentType === this.paymentTypes.CREDITCARD) {
          bookingData.stripe_payment_intent_id = this.stripePaymentMethodId ? this.stripePaymentMethodId : null;
        }

        bookingItems.push(bookingData);
      }
    }

    return bookingItems;
  }

  doBooking(): void {
    this.disableBookingButton = true;
    this.bookButtonLoader = true;
    this.bookingErrorMessage = undefined;

    const bookingItems = this.prepareBookingItems();
    this.processBooking(bookingItems);
  }

  private processBooking(bookingItems: AppointmentBookItem[]): void {
    if (this.isBookingDisabled) {
      LoggerService.error('Booking is disabled');
      LoggerService.table(bookingItems);
      this.disableBookingButton = false;
      this.bookButtonLoader = false;
      this.showStripeRedirectionLoader = false;
      return;
    }

    this.adyenResumeService.markBookingSent(this.partner?.id, this.adyenPaymentDetails?.session_id);

    this.bookingRequestInFlight = true;
    this.formService
      .bookAppointments(bookingItems)
      .pipe(
        // Unsubscribing on destroy also cancels a booking-lock resend that is still waiting.
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.bookingRequestInFlight = false)
      )
      .subscribe({
        next: (result: {
          success?: boolean;
          errors?: { message: string }[];
          appointments?: any[];
        }) => {
          this.adyenResumeService.releaseIfBookingSent(this.partner?.id);
          this.showStripeRedirectionLoader = false;
          if (result.errors && result.errors[0] && result.errors[0].message) {
            this.bookingErrorMessage = result.errors[0].message;
            this.disableBookingButton = false;
            this.bookButtonLoader = false;
            return;
          }

          if (result.success) {
            LoggerService.log('Appointment booking done before send postmessage');
            if (result?.appointments?.length > 0) {
              for (const item of result?.appointments) {
                this.successFullBookings.push({
                  landingPageLink: environment?.calioDashboardBaseUrl + '/appointments/' + item?.uuid,
                  bookingUUID: item?.uuid,
                  selectedStore: item?.store ?? item?.worker?.store,
                });
              }
            }

            // send booking success to event to parent window
            this.googleAnalyticsService.emitEventToParent(WINDOW_LISTENER_CONSTANTS.APPOINTMENT_BOOKING_DONE, result?.appointments);
            this.googleAnalyticsService.emitBookingEvent(this.gtmEvents.APPOINTMENT_BOOKING_STEP_SUCCESS);
            this.googleAnalyticsService.generateSessionUuid();

            this.utilService.updateLocalStorage(this.CUSTOMFIELDS_LS_NAME, this.customFieldValues);
            this.userState.meeting_type_id = undefined;
            this.userState.captcha_challenge = undefined;
            this.utilService.updateLocalStorage(this.USER_LS_NAME, Object.assign({}, this.userState));

            this.bookingSuccessEvent.emit();
            this.finalPage = true;

            // clear cart from ls and object
            this.localStorageService.removeItem(this.STATE_LS_NAME, () => { });
            this.localStorageService.removeItem(this.CART_LS_NAME, () => { });

            this.appointmentState = {
              store: null,
              services: [],
              relatedServices: [],
              worker: null,
              date: null,
              termins: null,
              day: null,
              leadGeneratorPostcode: null,
            };

            this.cart = [];
            this.calendarSlotsCountList = [];
          } else {
            this.finalPage = false;
          }
          this.priceAfterCouponReduction = undefined;
          this.cartVat.set({ byRate: [], totalCents: 0 });
          this.stripePaymentMethodId = undefined;
          this.paypalOrderDetail = undefined;
          this.saferPayPaymentDetails = undefined;
          // The Adyen session is spent too: the shopper has already been charged and the backend now refuses to claim a log that produced a booking, so re-sending it would only fail.
          this.adyenPaymentDetails = undefined;
          this.storageComplete = false;
          this.booked = true;
          this.disableBookingButton = false;
          this.isInvalidCoupon = false;
          this.bookButtonLoader = false;
          this.appointmentService.gtmStartTriggered = false;
          this.showStripeRedirectionLoader = false;

          if (
            this.partnerService.hasFeature('is_external_success_page_forwarding_enabled') &&
            this.partner.is_redirect_after_booking_enabled &&
            this.partner.successful_booking_redirect_url &&
            this.partner.redirect_after_booking_action === this.bookingRedirectionViews.BOOKING_SUMMARY_VIEW &&
            !this.globals.isInternal
          ) {
            this.helperService.redirectToUrl(this.partner.successful_booking_redirect_url);
          }

          this.localStorageService.setItem(LOCAL_STORAGE_CONSTANTS.LAST_PAYMENT_METHOD, this.paymentType, () => {
            LoggerService.log('LAST_PAYMENT_METHOD saved', this.paymentType);
          });
        },
        error: error => {
          // A paid booking still held by the booking lock after every resend is most likely being
          // completed: keep this tab's record that /book was sent, so a reload cannot start a
          // second payment beside it.
          const stillCompleting = isPaidBookingStillCompleting(error, !!this.adyenPaymentDetails);
          if (!stillCompleting) {
            this.adyenResumeService.releaseIfBookingSent(this.partner?.id);
          }
          LoggerService.error(error);
          this.isPaymentInProgress = false;
          this.showFullscreenOverlay = false;
          this.showRefundNotice = !!(this.stripePaymentMethodId || this.paypalOrderDetail || this.saferPayPaymentDetails || this.adyenPaymentDetails);
          // A lost answer is not a refusal: the booking may exist, so no refund is promised.
          this.refundNoticeKey = refundNoticeKeyFor(error, !!this.adyenPaymentDetails);
          this.refundTransactionId = this.stripePaymentMethodId
            || this.paypalOrderDetail?.paypal_order_id
            || this.saferPayPaymentDetails?.transaction_id
            || this.adyenPaymentDetails?.session_id;
          // After /book fails the payment tokens are dead — Stripe PI is already
          // captured (refunded async by BE cron), PayPal order is consumed, Saferpay
          // auth will expire. Invalidate locally and ask the Stripe component to
          // create a fresh PI so the next click can't reuse a dead token.
          const stripeWasUsed = !!this.stripePaymentMethodId;
          const adyenWasUsed = !!this.adyenPaymentDetails;
          const paypalOrSaferpayWasUsed = !!(this.paypalOrderDetail || this.saferPayPaymentDetails);
          // A paid booking whose answer was lost (connection dropped, 504) may have gone through.
          const outcomeUnknown = (paypalOrSaferpayWasUsed || adyenWasUsed) && isBookingOutcomeUnknown(error);
          // The backend will not claim this payment session again - which does not prove a booking.
          const sessionSpent = isPaymentSessionSpent(error);
          // The backend says this payment already backs a confirmed booking: that booking exists.
          const alreadyBooked = isAlreadyBookedWithPayment(error);
          this.stripePaymentMethodId = undefined;
          this.paypalOrderDetail = undefined;
          this.saferPayPaymentDetails = undefined;
          // The Adyen session is spent too: the shopper has already been charged and the backend now refuses to claim a log that produced a booking, so re-sending it would only fail.
          this.adyenPaymentDetails = undefined;
          // Reset so the next storeBookingItemsEvent goes false -> true and the
          // Stripe component's @Input setter actually fires handlePayment().
          // Without this, the second Book click updates the PI but never calls
          // confirmPayment() and the loader hangs.
          this.storageComplete = false;
          if (stripeWasUsed) {
            this.paymentService.recreatePaymentIntentSubject.next();
          }
          if (stillCompleting) {
            // Check the email, and NO "Pay again": the booking is most likely going through.
            this.paymentService.bookingStillCompletingSubject.next();
          } else if (alreadyBooked) {
            // The booking exists: its notice, the payment form kept down and NO "Pay again".
            this.paymentService.paymentAlreadyBookedSubject.next();
          } else if (sessionSpent) {
            // Handled like a lost answer, in its own words: check the email, then "Pay again".
            this.paymentService.paymentSessionSpentSubject.next();
          } else if (outcomeUnknown) {
            // The booking may exist, so the payment form must NOT come back on its own: that invited
            // a second payment beside a "check your email" notice. The customer re-arms it with
            // "Pay again" once they have checked.
            this.paymentService.bookingOutcomeUnknownSubject.next();
          } else if (paypalOrSaferpayWasUsed || adyenWasUsed) {
            // Brings the PayPal button / Saferpay launcher / Adyen Drop-in back into the DOM
            // so the customer can authorize a fresh payment for the retry. For Adyen this is
            // the ONLY route: a successful payment hides the payment options, which destroys
            // cw-adyen, so nothing is left to signal. Re-rendering it mints a fresh session.
            this.paymentService.resetPaymentSuccessStateSubject.next();
          }

          if (outcomeUnknown || sessionSpent || stillCompleting || alreadyBooked) {
            // Stay on the payment step, where cw-payment-methods says the booking could not be
            // confirmed and offers "Pay again". The final page's "could not be booked, please try
            // again" is exactly the wrong advice for a booking that may exist.
            this.bookingErrorMessage = undefined;
            this.disableBookingButton = false;
            this.bookButtonLoader = false;
            this.googleAnalyticsService.emitBookingEvent(this.gtmEvents.APPOINTMENT_BOOKING_STEP_FAILED);
            return;
          }

          if (this.showStripeRedirectionLoader) {
            this.showStripeRedirectionLoader = false;
            this.finalPage = false;
            this.booked = false;
            this.disableBookingButton = false;
            this.bookButtonLoader = false;
            this.currentView = this.appointmentConstant.PAYMENT;
            this.bookingErrorMessage = error?.error?.errors?.[0]?.message || error?.message;

            // Reset payment method ID to allow retry
            this.stripePaymentMethodId = undefined;
            this.paymentType = undefined;

            // Re-verify payment status to allow user to retry
            this.verifyOnlinePaymentStatus();

            // Scroll to error message
            setTimeout(() => {
              const element = getDomRoot(this.elementRef.nativeElement).getElementById('booking-error-message');
              element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 500);
            return;
          }
          if (error?.status === HttpStatusCode.InternalServerError) {
            this.translateService
              .get('common.noDateError')
              .pipe(takeWhile(() => this.alive))
              .subscribe({
                next: (translation) => {
                  this.finalPageError = translation;
                  this.finalPage = false;
                  this.booked = true;
                  this.disableBookingButton = false;
                  this.bookButtonLoader = false;
                }
              });
          } else {
            if (error?.error?.errors?.[0]?.message) {
              // A keyed Adyen refusal in the customer's language; anything else as the backend put it.
              const adyenMessageKey = adyenErrorMessageKey(error);
              this.bookingErrorMessage = adyenMessageKey
                ? this.translateService.instant(adyenMessageKey)
                : error.error.errors[0].message;
              this.disableBookingButton = false;
              this.bookButtonLoader = false;
              return;
            } else if (error?.error?.bookingErrors?.[0]?.message) {
              this.finalPage = false;
              this.bookingErrorMessage = error.error.bookingErrors.map(e => e.message);
            }
            this.finalPageError = error?.message;
            this.finalPage = false;
            this.booked = true;
            this.disableBookingButton = false;
            this.bookButtonLoader = false;
          }
          this.googleAnalyticsService.emitBookingEvent(this.gtmEvents.APPOINTMENT_BOOKING_STEP_FAILED);
          this.priceAfterCouponReduction = undefined;
          this.cartVat.set({ byRate: [], totalCents: 0 });
          this.isInvalidCoupon = false;
        }
      });
  }

  hideBookingAgainButton(): boolean {
    if (this.finalPage) {
      if (this.partner.hide_book_additional_appointment_button === 1) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  bookAppointmentAgain(): void {
    this.appointmentService.gtmStartTriggered = false;
    this.successFullBookings = [];
    this.initFormState();
    this.booked = false;
    this.currentView = this.appointmentConstant.APPOINTMENT;
    this.finalPage = false;
    this.finalPageError = undefined;
    this.backButtonDisabled = false;
    this.showAvailableAppointments = false;

    // Reset payment-related flags for new booking
    this.storageComplete = false;
    this.showRefundNotice = false;
    this.refundTransactionId = undefined;
    this.stripePaymentMethodId = undefined;
    this.paymentType = undefined;
    this.paypalOrderDetail = undefined;
    this.saferPayPaymentDetails = undefined;
    // The Adyen session is spent too: the shopper has already been charged and the backend now refuses to claim a log that produced a booking, so re-sending it would only fail.
    this.adyenPaymentDetails = undefined;
    this.coupon = undefined;
    this.finalCouponDiscount = 0;
    this.priceAfterCouponReduction = undefined;
    this.cartVat.set({ byRate: [], totalCents: 0 });
    this.isInvalidCoupon = false;
    this.totalCartPrice = 0;
  }

  goBack(screen = ''): void {
    if (this.adyenPaymentInFlight()) {
      // Leaving the payment step would tear down the payment the shopper may be completing now.
      return;
    }
    if (screen === this.appointmentConstant.APPOINTMENT) {
      this.appointmentState.date = null;
      this.showAvailableAppointments = false;
    } else {
      if (this.currentView === this.appointmentConstant.PERSONAL_INFO) {
        this.showAppointmentSlotGhostElement = true;
        this.freeAppointmentsCount = 0;
        this.monthChanged = false;
        this.currentView = this.appointmentConstant.APPOINTMENT;
        this.backButtonDisabled = false;
        this.paypalOrderDetail = undefined;
        this.saferPayPaymentDetails = undefined;
        // The Adyen session is spent too: the shopper has already been charged and the backend now refuses to claim a log that produced a booking, so re-sending it would only fail.
        this.adyenPaymentDetails = undefined;
        this.stripePaymentMethodId = undefined;
        this.refundTransactionId = undefined;
        this.isPaymentInProgress = false;
        this.showRefundNotice = false;
      } else if (this.currentView === this.appointmentConstant.PAYMENT) {
        this.currentView = this.appointmentConstant.PERSONAL_INFO;
      }
    }
  }

  prepareAnalyticsPayload(): AppointmentAnalyticsDao {
    const payload = new AppointmentAnalyticsDao();
    const store: { [key: string]: string | number } = {};
    const worker: { [key: string]: string | number } = {};
    const servicesName = [];

    // get store details if present
    store.id = this.store?.id;
    store.name = this.store?.name || undefined;

    // get worker details if present
    worker.id = this.worker.id;
    worker.email = this.worker?.email;
    worker.name = this.worker?.resource_name;

    // prepare service name string
    servicesName.push(this.service.name);

    // prepare final payload object
    if (this.appointmentState?.services) {
      payload.appointment_service_id = this.appointmentState.services.join(',');
    }

    if (servicesName.length) {
      payload.appointment_service_name = servicesName.join(',');
    }

    if (worker?.id) {
      payload.worker_id = worker.id;
      payload.resource_name = worker?.name;
      payload.worker_email = worker?.email;
    }

    if (store?.id) {
      payload.store_id = store.id;
      payload.store_name = store?.name;
    }

    LoggerService.log('[Analytics payload]', payload);
    return payload;
  }

  protected redirectToExternalUrl(): void {
    this.helperService.redirectToUrl(this.partner.successful_booking_redirect_url);
  }

  protected onTimezoneChange(timezone: string): void {
    this.calendarLoaded = false;
    this.userState.timezone = timezone;
    this.setupDates();
    this.customEventService.refreshCalendarEvent.emit();
  }

  protected submitBooking(): void {
    if (this.adyenPaymentInFlight()) {
      // Book is locked while the payment runs; a successful payment books by itself.
      return;
    }

    if (this.isReservationExpired) {
      this.bookingErrorMessage = this.translateService.instant(reservationExpiredMessageKey(this.isAdyenPaymentHeld()));
      return;
    }

    if (this.formService.imageUploaded === false) {
      LoggerService.warn('[Processing] Image is uploading...');
      this.widgetUtilService.markFormGroupTouched(this.appointmentForm);
      return;
    }

    if (this.appointmentForm.invalid) {
      this.widgetUtilService.handleFormInvalidState(this.appointmentForm, this.elementRef);
      this.validatedPayment();
      return;
    }

    // When a 100% coupon reduces the price to 0, switch to STORE payment
    // to avoid triggering Stripe when the payment element is hidden.
    if (this.totalCartPrice <= 0 || this.priceAfterCouponReduction <= 0) {
      this.paymentType = this.paymentTypes.STORE;
    }

    if (this.totalCartPrice > 0 && this.isServiceOnlinePaymentEnabled === 1) {
      if (this.paymentType === this.paymentTypes.SAFERPAY) {
        this.payUsingSaferpay();
      } else if (this.paymentType === this.paymentTypes.CREDITCARD) {
        this.payUsingStripe();
      } else {
        this.validateAndBooking();
      }
    } else {
      this.validateAndBooking();
    }
  }
}
